import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Inject, NgZone, NgModule } from '@angular/core';
import { from, Observable, of, timer, throwError, forkJoin, interval, merge, Subject, fromEvent, BehaviorSubject, race } from 'rxjs';
import { map, mergeMap, tap, catchError, switchMap, materialize, takeWhile, dematerialize, filter, first } from 'rxjs/operators';
import * as i2 from '@transparent/coretypes';
import { CWDetection, cleanSubscription, PLAYER_CONFIG, UIDUtil } from '@transparent/coretypes';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpResponse, HttpClientModule } from '@angular/common/http';
import { EV } from '@transparent/evapi';
import { CommonModule } from '@angular/common';

// https://developers.google.com/web/fundamentals/media/mse/seamless-playback
function getPaddings(arrayBuffer, sampleRate = 44100.0) {
  const SECONDS_PER_SAMPLE = 1 / 44100.0;
  // Gapless data is generally within the first 4096 bytes, so limit parsing.
  const byteStr = String.fromCharCode.apply(null, Array.from(new Uint8Array(arrayBuffer.slice(0, 4096))));
  let frontPadding = 0;
  let endPadding = 0;
  let realSamples = 0;
  // iTunes encodes the gapless data as hex strings like so:
  //
  //    'iTunSMPB[ 26 bytes ]0000000 00000840 000001C0 0000000000046E00'
  //    'iTunSMPB[ 26 bytes ]####### frontpad  endpad    real samples'
  //
  // The approach here elides the complexity of actually parsing MP4 atoms. It
  // may not work for all files without some tweaks.
  const iTunesDataIndex = byteStr.indexOf('iTunSMPB');
  if (iTunesDataIndex !== -1) {
    const frontPaddingIndex = iTunesDataIndex + 34;
    frontPadding = parseInt(byteStr.substr(frontPaddingIndex, 8), 16);
    const endPaddingIndex = frontPaddingIndex + 9;
    endPadding = parseInt(byteStr.substr(endPaddingIndex, 8), 16);
    const sampleCountIndex = endPaddingIndex + 9;
    realSamples = parseInt(byteStr.substr(sampleCountIndex, 16), 16);
  }
  // Xing padding is encoded as 24bits within the header.  Note: This code will
  // only work for Layer3 Version 1 and Layer2 MP3 files with XING frame counts
  // and gapless information.  See the following documents for more details:
  // http://www.codeproject.com/Articles/8295/MPEG-Audio-Frame-Header (2.3.1)
  // http://gingko.homeip.net/docs/file_formats/dxhead.html (FRAMES_FLAG)
  let xingDataIndex = byteStr.indexOf('Xing');
  if (xingDataIndex === -1) {
    xingDataIndex = byteStr.indexOf('Info');
  }
  if (xingDataIndex !== -1) {
    const frameCountIndex = xingDataIndex + 8;
    const frameCount = readInt(byteStr.substr(frameCountIndex, 4));
    // For Layer3 Version 1 and Layer2 there are 1152 samples per frame.
    realSamples = frameCount * 1152;
    xingDataIndex = byteStr.indexOf('LAME');
    if (xingDataIndex === -1) {
      xingDataIndex = byteStr.indexOf('Lame');
    }
    if (xingDataIndex === -1) {
      xingDataIndex = byteStr.indexOf('Lavf') + 7;
    }
    if (xingDataIndex !== -1) {
      const gaplessDataIndex = xingDataIndex + 21;
      const gaplessBits = readInt(byteStr.substr(gaplessDataIndex, 3));
      // Upper 12 bits are the front padding, lower are the end padding.
      frontPadding = gaplessBits >> 12;
      endPadding = gaplessBits & 0xFFF;
    }
    realSamples -= frontPadding + endPadding;
  }
  // console.log(realSamples + ' -- ' + frontPadding + ' -- ' + endPadding);
  return {
    audioDuration: realSamples * SECONDS_PER_SAMPLE,
    frontPaddingDuration: frontPadding * SECONDS_PER_SAMPLE,
    endPaddingDuration: endPadding * SECONDS_PER_SAMPLE
  };
}
function readInt(buffer) {
  let result = buffer.charCodeAt(0);
  for (let i = 1; i < buffer.length; ++i) {
    result <<= 8;
    result += buffer.charCodeAt(i);
  }
  return result;
}
const formats = {
  0x0001: 'lpcm',
  0x0003: 'lpcm'
};
class BinaryReader {
  constructor(dataView) {
    this.dataView = dataView;
    this.pos = 0;
  }
  remain() {
    return this.dataView.byteLength - this.pos;
  }
  skip(n) {
    this.pos += n;
  }
  uint8() {
    const data = this.dataView.getUint8(this.pos);
    this.pos += 1;
    return data;
  }
  int16() {
    const data = this.dataView.getInt16(this.pos, true);
    this.pos += 2;
    return data;
  }
  uint16() {
    const data = this.dataView.getUint16(this.pos, true);
    this.pos += 2;
    return data;
  }
  uint32() {
    const data = this.dataView.getUint32(this.pos, true);
    this.pos += 4;
    return data;
  }
  readString(n) {
    let data = '';
    for (let i = 0; i < n; i++) {
      data += String.fromCharCode(this.uint8());
    }
    return data;
  }
  pcm8() {
    const data = this.dataView.getUint8(this.pos) - 128;
    this.pos += 1;
    return data < 0 ? data / 128 : data / 127;
  }
  pcm8s() {
    const data = this.dataView.getUint8(this.pos) - 127.5;
    this.pos += 1;
    return data / 127.5;
  }
  pcm16() {
    const data = this.dataView.getInt16(this.pos, true);
    this.pos += 2;
    return data < 0 ? data / 32768 : data / 32767;
  }
  pcm16s() {
    const data = this.dataView.getInt16(this.pos, true);
    this.pos += 2;
    return data / 32768;
  }
  pcm24() {
    const x0 = this.dataView.getUint8(this.pos + 0);
    const x1 = this.dataView.getUint8(this.pos + 1);
    const x2 = this.dataView.getUint8(this.pos + 2);
    const xx = x0 + (x1 << 8) + (x2 << 16);
    const data = xx > 0x800000 ? xx - 0x1000000 : xx;
    this.pos += 3;
    return data < 0 ? data / 8388608 : data / 8388607;
  }
  pcm24s() {
    const x0 = this.dataView.getUint8(this.pos + 0);
    const x1 = this.dataView.getUint8(this.pos + 1);
    const x2 = this.dataView.getUint8(this.pos + 2);
    const xx = x0 + (x1 << 8) + (x2 << 16);
    const data = xx > 0x800000 ? xx - 0x1000000 : xx;
    this.pos += 3;
    return data / 8388608;
  }
  pcm32() {
    const data = this.dataView.getInt32(this.pos, true);
    this.pos += 4;
    return data < 0 ? data / 2147483648 : data / 2147483647;
  }
  pcm32s() {
    const data = this.dataView.getInt32(this.pos, true);
    this.pos += 4;
    return data / 2147483648;
  }
  pcm32f() {
    const data = this.dataView.getFloat32(this.pos, true);
    this.pos += 4;
    return data;
  }
  pcm64f() {
    const data = this.dataView.getFloat64(this.pos, true);
    this.pos += 8;
    return data;
  }
}
/**
 * Checks if data is in wav format
 */
function isBufferWav(data) {
  if (!(data instanceof BinaryReader)) {
    const dataView = new DataView(data);
    data = new BinaryReader(dataView);
  }
  return data.readString(4) === 'RIFF';
}
/**
 * Decodes wav
 */
function decodeWav(buffer) {
  const dataView = new DataView(buffer);
  const reader = new BinaryReader(dataView);
  if (!isBufferWav(reader)) {
    throw new TypeError('Invalid WAV file');
  }
  reader.uint32(); // skip file length
  if (reader.readString(4) !== 'WAVE') {
    throw new TypeError('Invalid WAV file');
  }
  let format = null;
  let audioData = null;
  do {
    const chunkType = reader.readString(4);
    const chunkSize = reader.uint32();
    switch (chunkType) {
      case 'fmt ':
        format = decodeFormat(reader, chunkSize);
        if (format instanceof Error) {
          throw format;
        }
        break;
      case 'data':
        audioData = decodeData(reader, chunkSize, format);
        if (audioData instanceof Error) {
          throw audioData;
        }
        break;
      default:
        reader.skip(chunkSize);
        break;
    }
  } while (audioData === null);
  return audioData;
}
function decodeFormat(reader, chunkSize) {
  const formatId = reader.uint16();
  if (!formats.hasOwnProperty(formatId)) {
    throw new TypeError('Unsupported format in WAV file: 0x' + formatId.toString(16));
  }
  const format = {
    formatId,
    floatingPoint: formatId === 0x0003,
    numberOfChannels: reader.uint16(),
    sampleRate: reader.uint32(),
    byteRate: reader.uint32(),
    blockSize: reader.uint16(),
    bitDepth: reader.uint16()
  };
  reader.skip(chunkSize - 16);
  return format;
}
function decodeData(reader, chunkSize, format) {
  chunkSize = Math.min(chunkSize, reader.remain());
  const length = Math.floor(chunkSize / format.blockSize);
  const numberOfChannels = format.numberOfChannels;
  const sampleRate = format.sampleRate;
  const channelData = new Array(numberOfChannels);
  for (let ch = 0; ch < numberOfChannels; ch++) {
    channelData[ch] = new Float32Array(length);
  }
  const retVal = readPCM(reader, channelData, length, format);
  if (retVal instanceof Error) {
    throw retVal;
  }
  return {
    numChannels: numberOfChannels,
    duration: length / sampleRate,
    sampleRate,
    soundData: channelData[0],
    type: 'wav'
  };
}
function readPCM(reader, channelData, length, format) {
  const bitDepth = format.bitDepth;
  const decoderOption = format.floatingPoint ? 'f' : '';
  const methodName = 'pcm' + bitDepth + decoderOption;
  if (!reader[methodName]) {
    return new TypeError('Not supported bit depth: ' + format.bitDepth);
  }
  const read = reader[methodName].bind(reader);
  const numberOfChannels = format.numberOfChannels;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numberOfChannels; ch++) {
      channelData[ch][i] = read();
    }
  }
  return null;
}

/**
 *Convert array of floats to Float32Array for faster performance
 */
function convertArrayToFloat32(bytes) {
  const pcm = new Float32Array(bytes.length);
  pcm.set(bytes);
  return pcm;
}
/**
 * Very primitive check of file for being ogg or not
 */
function isOgg$1(url) {
  return /\.ogg$/i.test(url);
}
/**
 * Very primitive check of file for being ogg or not
 */
function isWav(url) {
  return /\.wav$/i.test(url);
}
/**
 * Added 0.1 ms silent data before sound
 */
function addSilentData(pcm, slientData) {
  const bytes = new Float32Array(slientData.length + pcm.length + slientData.length);
  bytes.set(slientData);
  bytes.set(pcm, slientData.length);
  bytes.set(slientData, pcm.length + slientData.length);
  return bytes;
}
function createPauseEvent() {
  let evt = document.createEvent('Event');
  if (evt && evt.initEvent) {
    evt.initEvent('pause', true, true);
  } else {
    evt = new CustomEvent('pause', {
      bubbles: true,
      cancelable: true
    });
  }
  return evt;
}
/**
 * Creates audio buffer from given Float32Array
 */
function createAudioBuffer(context, float32Array, sampleRate) {
  const len = float32Array.length;
  let audioBuffer;
  if (context && len) {
    audioBuffer = context.createBuffer(1, len, sampleRate);
    if (typeof audioBuffer.copyToChannel === 'function') {
      audioBuffer.copyToChannel(float32Array, 0, 0);
    } else if (typeof audioBuffer.getChannelData === 'function') {
      audioBuffer.getChannelData(0).set(float32Array, 0);
    } else {
      audioBuffer = createAudioBufferInternal(float32Array, sampleRate);
    }
  } else {
    audioBuffer = createAudioBufferInternal(float32Array, sampleRate);
  }
  return audioBuffer;
}
function createAudioBufferInternal(float32Array, sampleRate) {
  return {
    duration: 0,
    length: float32Array.length,
    numberOfChannels: 1,
    sampleRate,
    copyFromChannel: (destination, channelNumber, startInChannel) => {},
    copyToChannel: (source, channelNumber, startInChannel) => {},
    getChannelData: channel => float32Array
  };
}
/**
 * Calculates buffer length
 */
function calculateBufferLength(pcmLength, sampleRate, numChannels) {
  return Math.floor(pcmLength / numChannels / (sampleRate / 22050));
}
/**
 * Calculate sound peak
 */
function calculateSoundPeak(soundData) {
  let max_peak = 0;
  let peakIndex = 0;
  for (let i = 0; i < soundData.length; i++) {
    if (soundData[i] > max_peak) {
      max_peak = soundData[i];
      peakIndex = i;
    }
  }
  return peakIndex;
}
/**
 * Get Cross browser Audio Context object
 */
function getAudioContext(win) {
  win = win || window;
  let ctx = win.aContext;
  if (!ctx) {
    const AContext = win.AudioContext || win.webkitAudioContext || win.mozAudioContext;
    if (AContext) {
      try {
        ctx = new AContext();
      } catch (e) {
        console.warn('Can not create context for decoding audio data!');
        ctx = null;
      }
      win.aContext = ctx;
    }
  }
  return ctx;
}
/**
 * fix time
 */
function fixTime(value, ourPeak, serverPeak, sampleRate) {
  if (isNaN(sampleRate) || ourPeak < 0) {
    return value;
  }
  // const delta = -((peak * sampleRate / sampleRate) - soundPeak) * 1. / sampleRate;
  // const delta = -(peak - soundPeak) / sampleRate;
  const delta = (ourPeak - serverPeak) / sampleRate;
  return value + delta;
}
class SNR {
  /**
   * Removes direct current components from the signal.
   */
  static dcRemove(xc) {
    const myFilter = (inVector, zi) => {
      const a = -0.98;
      const len = inVector.length;
      const out = new Array(inVector.length);
      out[0] = zi + inVector[0];
      for (let i = 1; i < len; i++) {
        out[i] = inVector[i] - inVector[i - 1] - a * out[i - 1];
      }
      const zo1 = -inVector[inVector.length - 1] - a * out[out.length - 1];
      return {
        out,
        zo: zo1
      };
    };
    const xt = new Array(3);
    xt[0] = 2 * xc[0] - xc[3];
    xt[1] = 2 * xc[0] - xc[2];
    xt[2] = 2 * xc[0] - xc[1];
    let zo;
    const t1 = myFilter(xt, -xt[0]);
    zo = t1.zo;
    const t2 = myFilter(xc, zo);
    const yc2 = t2.out;
    zo = t2.zo;
    xt[0] = 2 * xc[xc.length - 1] - xc[xc.length - 2];
    xt[1] = 2 * xc[xc.length - 1] - xc[xc.length - 3];
    xt[2] = 2 * xc[xc.length - 1] - xc[xc.length - 4];
    const t3 = myFilter(xt, zo);
    const yc3 = t3.out;
    yc3.reverse();
    const t4 = myFilter(yc3, -yc3[yc3.length - 1]);
    zo = t4.zo;
    yc2.reverse();
    const t5 = myFilter(yc2, zo);
    const yc5 = t5.out;
    yc5.reverse();
    return yc5;
  }
}
function Mean(samples) {
  return samples.reduce((a, b) => a + b, 0) / samples.length;
}
function MeanPow(samples) {
  return samples.reduce((a, b) => a + Math.pow(b, 2), 0) / samples.length;
}
function BlockAverage(samples, blockSize) {
  let means = [];
  for (let i = 0; i < samples.length; i += blockSize) {
    means.push(MeanPow(samples.slice(i, i + blockSize)));
  }
  means = means.map(a => Math.round(a * 1000000) / 1000000).filter(a => a !== 0);
  return means;
}
function calculateSNRA(samples, sampleRate = 22050) {
  const step = Math.round(sampleRate * 0.01) * 2;
  const m = Mean(samples);
  samples = samples.map(a => a - m);
  const arr = BlockAverage(samples, step);
  const N = arr.reduce((a, b) => Math.min(a, b), 1);
  const S = MeanPow(samples);
  const SNR = 10 * Math.log10(S / N);
  return SNR;
}

/*eslint-disable  @typescript-eslint/naming-convention*/
let UserSoundQuality = /*#__PURE__*/(() => {
  class UserSoundQuality {
    static {
      this.HIGH_GAIN = 'high_gain';
    }
    static {
      this.LOW_SNR = 'low_snr';
    }
    static {
      this.OK = 'ok';
    }
    static check(X, snr_threshold) {
      const result = {
        result: UserSoundQuality.OK,
        snr: -1
      };
      const dcX = SNR.dcRemove(X);
      const snr = calculateSNRA(dcX);
      result.snr = snr;
      if (snr < snr_threshold) {
        result.result = UserSoundQuality.LOW_SNR;
      }
      return result;
    }
  }
  return UserSoundQuality;
})();
/*eslint-disable  @typescript-eslint/naming-convention*/
let SCSActivityType = /*#__PURE__*/(() => {
  class SCSActivityType {
    static {
      this.MC2 = 'mc2';
    }
    static {
      this.PRONUNCIATION = 'pron';
    }
    static {
      this.SELF_REPORTING = 'selfreporting';
    }
    static {
      this.CONVERSATION = 'convsay';
    }
    static {
      this.LISTENING_RECOGNITION = 'lrecognition';
    }
    static {
      this.MATCHING = 'matching';
    }
  }
  return SCSActivityType;
})();
/**
 * Trims silence ranges in the beginning and in the end of the given wave data
 */
function trimSilence(input, sampleRate) {
  const padding = Math.round(sampleRate * 0.1);
  const vad = calculateVoicePositions(input, sampleRate);
  vad.start -= padding;
  vad.end += padding;
  vad.start = Math.max(0, vad.start);
  vad.end = Math.max(0, vad.end);
  vad.end = vad.end > 0 ? vad.end : input.length;
  return input.subarray(vad.start, vad.end);
}
/**
 * Calculate VAD positions
 */
function calculateVoicePositions(input, sampleRate) {
  let i;
  let k;
  const chunk = 256;
  const len = input.length;
  for (i = 0; i < len; i += chunk) {
    const res = autoCorrelate(input.slice(i, i + chunk), sampleRate);
    if (res.correlation !== -1) {
      i = i + res.offset;
      break;
    }
  }
  for (k = len; k > 0; k -= chunk) {
    const res = autoCorrelate(input.slice(k - chunk, k), sampleRate);
    if (res.correlation !== -1) {
      k = k + res.offset;
      break;
    }
  }
  return {
    start: i,
    end: k
  };
}
function autoCorrelate(buf, sampleRate) {
  const GOOD_ENOUGH_CORRELATION = 0.9; // this is the "bar" for how close a correlation needs to be
  const SIZE = buf.length;
  const MAX_SAMPLES = Math.floor(SIZE / 2);
  let best_offset = -1;
  let best_correlation = 0;
  let rms = 0;
  let foundGoodCorrelation = false;
  const correlations = new Array(MAX_SAMPLES);
  for (let i = 0; i < SIZE; i++) {
    const val = buf[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) {
    // not enough signal
    return {
      correlation: -1,
      offset: 0
    };
  }
  let lastCorrelation = 1;
  for (let offset = 0; offset < MAX_SAMPLES; offset++) {
    let correlation = 0;
    for (let i = 0; i < MAX_SAMPLES; i++) {
      correlation += Math.abs(buf[i] - buf[i + offset]);
    }
    correlation = 1 - correlation / MAX_SAMPLES;
    correlations[offset] = correlation; // store it, for the tweaking we need to do below.
    if (correlation > GOOD_ENOUGH_CORRELATION && correlation > lastCorrelation) {
      foundGoodCorrelation = true;
      if (correlation > best_correlation) {
        best_correlation = correlation;
        best_offset = offset;
      }
    } else if (foundGoodCorrelation) {
      // short-circuit - we found a good correlation, then a bad one, so we'd just be seeing copies from here.
      // Now we need to tweak the offset - by interpolating between the values to the left and right of the
      // best offset, and shifting it a bit.  This is complex, and HACKY in this code (happy to take PRs!) -
      // we need to do a curve fit on correlations[] around best_offset in order to better determine precise
      // (anti-aliased) offset.
      // we know best_offset >=1,
      // since foundGoodCorrelation cannot go to true until the second pass (offset=1), and
      // we can't drop into this clause until the following pass (else if).
      const shift = (correlations[best_offset + 1] - correlations[best_offset - 1]) / correlations[best_offset];
      return {
        correlation: sampleRate / (best_offset + 8 * shift),
        offset: best_offset
      };
    }
    lastCorrelation = correlation;
  }
  if (best_correlation > 0.01) {
    return {
      correlation: sampleRate / best_offset,
      offset: best_offset
    };
  }
  return {
    correlation: -1,
    offset: 0
  };
}
function base64ToArrayBuffer(dataURI) {
  const BASE64_MARKER = ';base64,';
  const base64Index = dataURI.indexOf(BASE64_MARKER);
  const base64Str = base64Index !== -1 ? dataURI.substring(base64Index + BASE64_MARKER.length) : dataURI;
  const raw = window.atob(base64Str);
  const rawLength = raw.length;
  const arr = new Uint8Array(new ArrayBuffer(rawLength));
  for (let i = 0; i < rawLength; i++) {
    arr[i] = raw.charCodeAt(i);
  }
  return arr.buffer;
}
/**
 */
function floatTo16BitPCM(output, offset, input, channels = 1, isLittleEnd = true) {
  const len = (input || []).length;
  for (let i = 0; i < len; i++) {
    let s = Math.max(-1, Math.min(1, input[i]));
    s = s < 0 ? s * 0x8000 : s * 0x7FFF;
    for (let j = 0; j < channels; j++, offset += 2) {
      output.setInt16(offset, s, isLittleEnd);
    }
  }
}
/**
 */
function floatTo8BitPCM(output, offset, input, channels = 1) {
  const len = (input || []).length;
  for (let i = 0; i < len; i++) {
    let s = Math.max(-1, Math.min(1, input[i]));
    s = Math.max(0, Math.min(255, s < 0 ? s * 0x80 : s * 0x7F));
    for (let j = 0; j < channels; j++, offset++) {
      output.setUint8(offset, s);
    }
  }
}
/**
 * Encodes pcm array as wav file
 */
function encodeWAV(samples, mono, samplingRate = 22050, bits = 16, isLittleEnd = true) {
  if (samples && samples.length > 0) {
    const numChannels = mono ? 1 : 2;
    // jshint bitwise:false
    const blockAlign = numChannels * bits >> 3;
    let dataLen = bits === 8 ? samples.length : samples.length * 2;
    dataLen *= numChannels;
    // jshint bitwise:true
    const RIFF = 0x46464952; // Resource Interchange File Format
    const WAVE = 0x45564157; // Wave
    const FMT = 0x20746D66; // Format
    const DATA = 0x61746164; // Audio Data
    const buffer = new ArrayBuffer(44 + dataLen);
    const view = new DataView(buffer);
    view.setUint32(0, RIFF, true);
    /* file length */
    view.setUint32(4, 32 + dataLen, true);
    /* RIFF type */
    view.setUint32(8, WAVE, true);
    view.setUint32(12, FMT, true);
    /* format chunk length */
    view.setUint32(16, 16, true);
    /* sample format (raw) */
    view.setUint16(20, 1, true);
    /* channel count */
    view.setUint16(22, numChannels, true);
    /* sample rate */
    view.setUint32(24, samplingRate, true);
    /* byte rate (sample rate * block align) */
    view.setUint32(28, samplingRate * blockAlign, true);
    /* block align (channel count * bytes per sample) */
    view.setUint16(32, blockAlign, true);
    /* bits per sample */
    view.setUint16(34, bits, true);
    view.setUint32(36, DATA, true);
    view.setUint32(40, dataLen, true);
    /* data chunk length */
    if (bits === 8) {
      floatTo8BitPCM(view, 44, samples, numChannels);
    } else {
      floatTo16BitPCM(view, 44, samples, numChannels, isLittleEnd);
    }
    return view;
  } else {
    return null;
  }
}
/**
 * Encodes pcm array as wav file
 */
function encodeWAV2(samples, samplingRate = 22050, numChannels = 1) {
  return encodeWAV(samples, numChannels === 1, samplingRate);
}
/**
 * Exports pcm data as blob
 */
function exportWAV(soundData) {
  const dataView = encodeWAV(soundData, true);
  return new Blob([dataView], {
    type: 'audio/wav'
  });
}
/**
 * Converts pcm data to base64 string and returns it
 */
function exportBase64(soundData) {
  if (!soundData || !soundData.length) {
    return null;
  }
  const dataView = encodeWAV(soundData, true);
  return 'data:audio/wav;base64,' + encode64(dataView.buffer);
}
/**
 * Parses binary wav and gets pcm data
 */
function getFloatDataFromWav(buffer) {
  const dataView = new DataView(buffer);
  const chunkSize = dataView.getInt32(40, true);
  const len = chunkSize / 2;
  const tempArray = new Float32Array(len);
  for (let j = 0, i = 44; j < len; j++, i += 2) {
    tempArray[j] = dataView.getInt16(i, true) / 32768.0;
  }
  return tempArray;
}
/**
 * Converts string of pcm data to base64 strings
 */
function encode64(arrayBuffer) {
  let base64 = '';
  const encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let bytes;
  if (arrayBuffer instanceof Array) {
    // this one should be array of bytes
    bytes = arrayBuffer;
  } else {
    bytes = typeof window.Uint8Array2 === 'function' ? new window.Uint8Array2(arrayBuffer) : new Uint8Array(arrayBuffer);
  }
  const byteLength = bytes instanceof Uint8Array ? bytes.byteLength : bytes.length;
  const byteRemainder = byteLength % 3;
  const mainLength = byteLength - byteRemainder;
  let a;
  let b;
  let c;
  let d;
  let chunk;
  // Main loop deals with bytes in chunks of 3
  // jshint bitwise:false
  for (let i = 0; i < mainLength; i = i + 3) {
    // Combine the three bytes into a single integer
    chunk = bytes[i] << 16 | bytes[i + 1] << 8 | bytes[i + 2];
    // Use bitmasks to extract 6-bit segments from the triplet
    a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
    b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
    c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
    d = chunk & 63; // 63       = 2^6 - 1
    // Convert the raw binary segments to the appropriate ASCII encoding
    base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
  }
  // Deal with the remaining bytes and padding
  if (byteRemainder === 1) {
    chunk = bytes[mainLength];
    a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2
    // Set the 4 least significant bits to zero
    b = (chunk & 3) << 4; // 3   = 2^2 - 1
    base64 += encodings[a] + encodings[b] + '==';
  } else if (byteRemainder === 2) {
    chunk = bytes[mainLength] << 8 | bytes[mainLength + 1];
    a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
    b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4
    // Set the 2 least significant bits to zero
    c = (chunk & 15) << 2; // 15    = 2^4 - 1
    base64 += encodings[a] + encodings[b] + encodings[c] + '=';
  }
  return base64;
}
/**
 * Array buffer to base64
 */
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  bytes.forEach(val => binary += String.fromCharCode(val));
  return window.btoa(binary);
}
/**
 * Get data length by seconds
 */
function getDataLengthBySeconds(duration) {
  return duration * 22050 | 0;
}
/**
 * Trim PCM bytes
 */
function trimPCMBytes(pcmBytes, start, end) {
  start = Math.floor(start);
  end = Math.floor(end);
  end = end === 0 ? pcmBytes.length : end;
  if (start > end) {
    start = start + end;
    end = start - end;
    start = start - end;
  }
  const arrLen = end - start;
  const trimmed = new Float32Array(arrLen);
  trimmed.set(pcmBytes.subarray(start, end));
  return trimmed;
}
/**
 * Trim PCM bytes by time
 */
function trimPCMBytesByTime(pcmBytes, start, end, sampleRate = 22050) {
  if (end <= 0) {
    end = pcmBytes.length / sampleRate;
  }
  start = Math.max(0, start);
  const posStart = start * sampleRate | 0;
  const posEnd = end * sampleRate | 0;
  const newLen = posEnd - posStart | 0;
  if (newLen <= 0) {
    return pcmBytes;
  }
  const trimmed = new Float32Array(newLen);
  trimmed.set(pcmBytes.subarray(posStart, posEnd));
  return trimmed;
}
/**
 * Create wav file from Float32Array and generates temporary base64 or blob url for it
 */
function createUrlFromPCM(float32Array) {
  if (window.URL && typeof window.URL.createObjectURL === 'function') {
    return window.URL.createObjectURL(exportWAV(float32Array));
  } else {
    return exportBase64(float32Array);
  }
}
function revokeObjectURL(objectUrl) {
  window.URL.revokeObjectURL(objectUrl);
}
/**
 * Compares two float32 arrays
 */
function compareFloatArrays(a, b) {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < b.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
const WavUtil = {
  base64ToArrayBuffer,
  floatTo16BitPCM,
  floatTo8BitPCM,
  encodeWAV,
  encodeWAV2,
  exportWAV,
  exportBase64,
  getFloatDataFromWav,
  encode64,
  arrayBufferToBase64,
  getDataLengthBySeconds,
  trimPCMBytes,
  trimPCMBytesByTime,
  createUrlFromPCM,
  revokeObjectURL,
  compareFloatArrays
};
const SAMPLE_RATE_22 = 22050;
const SAMPLE_RATE_44 = 44100;
let AudioResamplerService = /*#__PURE__*/(() => {
  class AudioResamplerService {
    constructor() {
      this.tailExists = false;
    }
    clean() {
      this.lastOutput = null;
      this.outputBuffer = null;
    }
    /**
     * Resample given audio buffer
     */
    resample(audioBuffer) {
      const win = window;
      const numFrames = audioBuffer.length * SAMPLE_RATE_22 / audioBuffer.sampleRate;
      let offlineContext;
      try {
        if (win.OfflineAudioContext) {
          offlineContext = new win.OfflineAudioContext(1, numFrames, 22050);
        } else if (win.webkitOfflineAudioContext) {
          // safari for ios only supports 44100
          const sr = CWDetection.isSafari && safariVersion() >= 14 ? 22050 : 16000;
          offlineContext = new win.webkitOfflineAudioContext(1, numFrames, sr);
        }
      } catch (e) {}
      if (!offlineContext) {
        return this.resampleForIE(audioBuffer.getChannelData(0), audioBuffer.sampleRate);
      }
      const bufferSource = offlineContext.createBufferSource();
      bufferSource.buffer = audioBuffer;
      bufferSource.connect(offlineContext.destination);
      bufferSource.start(0);
      const disconnect = () => {
        if (bufferSource && offlineContext) {
          try {
            bufferSource.disconnect(offlineContext.destination);
            bufferSource.buffer = null;
          } catch (e) {}
        }
      };
      const promise = offlineContext.startRendering();
      if (isPromise(promise)) {
        return from(promise).pipe(map(renderedBuffer => {
          disconnect();
          return renderedBuffer.getChannelData(0);
        }));
      }
      return new Observable(observer => {
        offlineContext.oncomplete = evt => {
          observer.next(evt.renderedBuffer.getChannelData(0));
          observer.complete();
        };
        return () => {
          disconnect();
          offlineContext.oncomplete = null;
        };
      });
    }
    /**
     *
     */
    resampleOthers(pcm, sampleRate, numChannels) {
      this.outputBufferSize = calculateBufferLength(pcm.length, sampleRate, numChannels);
      this.outputBuffer = new Float32Array(this.outputBufferSize);
      this.lastOutput = new Float32Array(numChannels);
      this.ratioWeight = sampleRate / SAMPLE_RATE_22;
      if (sampleRate === SAMPLE_RATE_22) {
        const len = pcm.length / 2 | 0;
        const arr = new Float32Array(len);
        for (let i = 0, j = 0; i < pcm.length && j < len; i += 2, j++) {
          arr[j] = (pcm[i] + pcm[i + 1]) / 2;
        }
        return arr;
      } else if (sampleRate < SAMPLE_RATE_22) {
        this.lastWeight = 1;
        return this.upSample(pcm);
      } else {
        this.tailExists = false;
        this.lastWeight = 0;
        return this.downSample(pcm);
      }
    }
    /**
     * Resample given audio buffer
     */
    resampleForIE(pcm, sampleRate) {
      return new Observable(observer => {
        let float32Array;
        const numChannels = 1;
        const resLen = pcm.length / numChannels / (sampleRate / SAMPLE_RATE_22) | 0;
        if (sampleRate === SAMPLE_RATE_22) {
          // do nothing
          float32Array = new Float32Array(pcm.length);
          float32Array.set(pcm);
        } else if (sampleRate === SAMPLE_RATE_44) {
          // simple downscale
          float32Array = new Float32Array(resLen);
          const len = pcm.length;
          for (let i = 0, j = 0; i < len; j++, i += 2) {
            float32Array[j] = (pcm[i] + pcm[i + 1]) / 2;
          }
        } else {
          float32Array = this.resampleOthers(pcm, sampleRate, numChannels);
        }
        observer.next(float32Array);
        observer.complete();
      });
    }
    /**
     * Up sample auido
     */
    upSample(buffer) {
      const lastOutput = this.lastOutput;
      let bufferLength = buffer.length;
      const outLength = this.outputBufferSize;
      const ratioWeight = this.ratioWeight;
      let weight = this.lastWeight;
      let firstWeight = 0;
      let secondWeight = 0;
      let outputOffset = 0;
      const outputBuffer = this.outputBuffer;
      for (; weight < 1; weight += ratioWeight) {
        secondWeight = weight % 1;
        firstWeight = 1 - secondWeight;
        outputBuffer[outputOffset++] = lastOutput[0] * firstWeight + buffer[0] * secondWeight;
      }
      weight -= 1;
      let sourceOffset = Math.floor(weight) * 1;
      for (bufferLength -= 1; outputOffset < outLength && sourceOffset < bufferLength;) {
        secondWeight = weight % 1;
        firstWeight = 1 - secondWeight;
        outputBuffer[outputOffset++] = buffer[sourceOffset] * firstWeight + buffer[sourceOffset + 1] * secondWeight;
        weight += ratioWeight;
        sourceOffset = Math.floor(weight) * 1;
      }
      return outputBuffer;
    }
    /**
     *
     */
    downSample(buffer) {
      const lastOutput = this.lastOutput;
      const bufferLength = buffer.length;
      const outLength = this.outputBufferSize;
      const ratioWeight = this.ratioWeight;
      let weight = 0;
      let output0 = 0;
      let actualPosition = 0;
      let amountToNext = 0;
      let alreadyProcessedTail = !this.tailExists;
      this.tailExists = false;
      const outputBuffer = this.outputBuffer;
      let outputOffset = 0;
      let currentPosition = 0;
      do {
        if (alreadyProcessedTail) {
          weight = ratioWeight;
          output0 = 0;
        } else {
          weight = this.lastWeight;
          output0 = lastOutput[0];
          alreadyProcessedTail = true;
        }
        while (weight > 0 && actualPosition < bufferLength) {
          amountToNext = 1 + actualPosition - currentPosition;
          if (weight >= amountToNext) {
            output0 += buffer[actualPosition++] * amountToNext;
            currentPosition = actualPosition;
            weight -= amountToNext;
          } else {
            output0 += buffer[actualPosition] * weight;
            currentPosition += weight;
            weight = 0;
            break;
          }
        }
        if (weight === 0) {
          outputBuffer[outputOffset++] = output0 / ratioWeight;
        } else {
          this.lastWeight = weight;
          lastOutput[0] = output0;
          this.tailExists = true;
          break;
        }
      } while (actualPosition < bufferLength && outputOffset < outLength);
      return outputBuffer;
    }
    static {
      this.ɵfac = function AudioResamplerService_Factory(t) {
        return new (t || AudioResamplerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AudioResamplerService,
        factory: AudioResamplerService.ɵfac
      });
    }
  }
  return AudioResamplerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isPromise(value) {
  return value && typeof value.subscribe !== 'function' && typeof value.then === 'function';
}
function safariVersion() {
  const ua = window.navigator.userAgent;
  return +ua.match(/version\/\d+/ig)[0].replace(/version\//i, '');
}
let AudioLoaderService = /*#__PURE__*/(() => {
  class AudioLoaderService {
    constructor(http, resampler) {
      this.http = http;
      this.resampler = resampler;
      document.addEventListener('click', this.onUserGesture.bind(this), true);
      document.addEventListener('touchend', this.onUserGesture.bind(this), true);
    }
    clean() {
      document.removeEventListener('click', this.onUserGesture, true);
      document.removeEventListener('touchend', this.onUserGesture, true);
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch();
      }
      this.audioContext = null;
    }
    /**
     * Load given mp3 or wav file and convert it to PCM data
     */
    load(url) {
      return this.http.get(url, {
        responseType: 'arraybuffer'
      }).pipe(mergeMap(buffer => {
        if (isBufferWav(buffer)) {
          const audioData = decodeWav(buffer);
          audioData.url = url;
          return of(audioData);
        } else {
          return this.decodeWithContext(buffer, url);
        }
      }));
    }
    loadAudio(url) {
      return this.load(url);
    }
    loadRecordedSound(url) {
      let obs;
      if (isWav(url)) {
        obs = this.http.get(url, {
          responseType: 'arraybuffer'
        });
      } else {
        obs = of(base64ToArrayBuffer(url)).pipe(tap(() => url = ''));
      }
      return obs.pipe(map(buffer => {
        const res = {
          numChannels: 1,
          duration: 0,
          sampleRate: 22050,
          soundData: new Float32Array(0),
          snr: UserSoundQuality.LOW_SNR,
          type: 'pcm'
        };
        if (isBufferWav(buffer)) {
          const audioData = decodeWav(buffer);
          audioData.url = url;
          let data = audioData.soundData;
          const trimmed = trimSilence(data, 22050);
          if (trimmed.length > 0) {
            data = trimmed;
          }
          res.duration = data.length / 22050;
          res.soundData = data;
          res.snr = trimmed.length > 0 ? '' : UserSoundQuality.LOW_SNR;
          res.hasSound = trimmed.length > 0;
          res.url = url;
          res.type = 'wav';
        }
        return res;
      }));
    }
    /**
     * Decodes buffer with mp3 decoder
     */
    decodeWithMp3Decoder(buffer, url) {
      return new Observable(observer => {
        const mp3 = window['mp3'] || {};
        const decoder = mp3.Mp3Decoder;
        if (decoder) {
          decoder.decodeMp3(buffer, (data, err) => {
            if (err) {
              observer.error(err);
            } else {
              data.url = url;
              data.paddings = getPaddings(buffer, data.sampleRate);
              observer.next(data);
            }
            observer.complete();
          });
        } else {
          observer.error('Mp3 library not loaded!!!');
          observer.complete();
        }
      });
    }
    /**
     * Decodes buffer with AudioContext
     */
    decodeWithContext(buffer, url) {
      if (!this.audioContext) {
        this.audioContext = getAudioContext();
      }
      const type = isOgg(buffer) ? 'ogg' : 'mp3';
      if (window['mp3'] && type === 'mp3') {
        return this.decodeWithMp3Decoder(buffer, url);
      }
      return new Observable(observer => {
        if (!this.audioContext) {
          observer.error('Cannot create Audio Context');
          observer.complete();
        }
        const promise = this.audioContext.decodeAudioData(buffer, decodedData => {
          let sub; // ReferenceError: Cannot access before initialization
          sub = this.resampler.resample(decodedData).subscribe(fa => {
            const audioData = {
              numChannels: 1,
              duration: decodedData.duration,
              sampleRate: 22050,
              soundData: fa,
              url,
              type
            };
            sub = cleanSubscription(sub);
            observer.next(audioData);
            observer.complete();
          }, err => {
            sub = cleanSubscription(sub);
            observer.error(err);
            observer.complete();
          });
        }, err => {
          observer.error(err);
          observer.complete();
        });
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => null);
        }
      });
    }
    onUserGesture() {
      this.audioContext = this.audioContext || getAudioContext();
      if (this.audioContext && ['interrupted', 'suspended'].includes(this.audioContext.state)) {
        try {
          this.audioContext.resume().catch();
        } catch (e) {
          console.warn(`New context created with ${this.audioContext.state} state. Things can go wrong...`);
        }
      }
    }
    static {
      this.ɵfac = function AudioLoaderService_Factory(t) {
        return new (t || AudioLoaderService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(AudioResamplerService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AudioLoaderService,
        factory: AudioLoaderService.ɵfac
      });
    }
  }
  return AudioLoaderService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isOgg(buffer) {
  const z = new Uint8Array(buffer, 0, 4);
  return z[0] === 79 && z[1] === 103 && z[2] === 103 && z[3] === 83;
}

/**
 * Gets native player
 */
function getMobilePlayer(win) {
  const topWin = getTopWindow(win);
  return topWin.upAudioPlayer ? topWin.upAudioPlayer : null;
}
/**
 * Gets web audio player
 */
function getAudioPlayer(win) {
  const topWin = getTopWindow(win);
  return topWin.audioPlayer ? topWin.audioPlayer : null;
}
/**
 * Utility method for finding mobile webview recorder
 */
function getNativeRecorder(win) {
  const topWin = getTopWindow(win);
  return topWin.upSoundRecorder ? topWin.upSoundRecorder : null;
}
/**
 * Returns parent window
 */
function getTopWindow(win) {
  const newWin = win.parent;
  try {
    newWin.hasMic = true; // just dummy access test
  } catch (e) {
    return win;
  }
  return newWin;
}
/**
 * Returns parent window
 */
function getHighestWindow(win) {
  let newWin = win;
  while (newWin.parent !== newWin.top) {
    const oldWin = newWin;
    try {
      newWin = newWin.parent;
      newWin.hasMic = true; // just dummy access test
    } catch (e) {
      newWin = oldWin;
      break;
    }
  }
  return newWin;
}
const WindowUtil = {
  getMobilePlayer,
  getAudioPlayer,
  getNativeRecorder,
  getTopWindow,
  getHighestWindow
};
class NativeCallback {
  /**
   * @Constructor
   */
  constructor(zone) {
    this.zone = zone;
  }
  clean() {
    this.onSoundDataLoadCompleteChange = null;
    this.onSlowSoundGeneratedChange = null;
    this.onErrorChange = null;
  }
  /**
   * SoundRecorder callback
   */
  onNativeCallback(name, data) {
    if (name === 'soundDataLoadComplete') {
      if (typeof this.onSoundDataLoadCompleteChange === 'function') {
        this.zone.run(() => this.onSoundDataLoadCompleteChange(data.url));
      }
    } else if (name === 'slowSoundGenerated') {
      if (typeof this.onSlowSoundGeneratedChange === 'function') {
        this.zone.run(() => this.onSlowSoundGeneratedChange(data));
      }
    } else if (name === 'error') {
      if (typeof this.onErrorChange === 'function') {
        this.zone.run(() => this.onErrorChange(data));
      }
    }
  }
}

/**
 * A `Table` backed by a `Cache`.
 */
class IdxDbStoreTable {
  constructor(storeName, dbName, dbVersion = 4) {
    this.storeName = storeName;
    this.dbName = dbName;
    this.dbVersion = dbVersion;
    this.storeName = storeName;
    this.openSub = this.openDurationsDb(dbName, dbVersion).subscribe(value => this.db = value);
  }
  clean() {
    this.openSub = cleanSubscription(this.openSub);
    this.db = null;
  }
  read(key) {
    if (!this.db) {
      return of('');
    }
    return this.getDbStore().pipe(mergeMap(store => {
      return new Observable(obs => {
        key = this.normalizeKey(key);
        const req = store.get(key);
        req.onsuccess = evt => {
          const res = evt.target.result;
          const dur = res === Infinity || res === 'Infinity' || isNaN(res) || res === 'null' ? '' : res;
          obs.next(dur);
          obs.complete();
        };
        req.onerror = evt => {
          console.log('read: ', evt.target.errorCode);
          obs.next('');
          obs.complete();
        };
      });
    }));
  }
  put(key, value) {
    if (!this.db) {
      return of('');
    }
    return this.getDbStore().pipe(mergeMap(store => {
      return new Observable(obs => {
        const req = store.put(value, this.normalizeKey(key));
        req.onsuccess = () => {
          obs.next(key);
          obs.complete();
        };
        req.onerror = evt => {
          console.log('write: ', evt.target.errorCode);
          obs.next(key);
          obs.complete();
        };
      });
    }));
  }
  getDbStore() {
    let transaction;
    try {
      transaction = this.db.transaction(this.storeName, 'readwrite');
    } catch (e) {
      return this.openDurationsDb(this.dbName, this.dbVersion).pipe(map(value => {
        this.db = value;
        return this.db.transaction(this.storeName, 'readwrite').objectStore(this.storeName);
      }));
    }
    return of(transaction.objectStore(this.storeName));
  }
  normalizeKey(key) {
    return (key || '').split('?')[0];
  }
  openDurationsDb(name, version) {
    const win = window;
    const indexedDB = win.indexedDB || win['mozIndexedDB'] || win['webkitIndexedDB'];
    if (!indexedDB) {
      return of(null);
    }
    return new Observable(obs => {
      const req = indexedDB.open(name, version);
      req.onsuccess = evt => {
        const db = evt.target.result;
        if (db.objectStoreNames.length) {
          obs.next(db);
        } else {
          obs.error('DB not opened!!!');
        }
        obs.complete();
      };
      req.onerror = evt => {
        if (evt.target.error && evt.target.error.code === 11) {
          console.log('openDb: ', evt.target.error.code);
          obs.next(null);
        } else {
          console.log('openDb: ', evt.target.errorCode);
          obs.error(evt);
        }
        obs.complete();
      };
      req.onupgradeneeded = evt => {
        const db = evt.target.result;
        if (!db.objectStoreNames.length) {
          db.transaction.oncomplete = () => obs.next(db);
          db.transaction.onerror = e => obs.error(e);
          db.createObjectStore('durations');
        }
      };
    });
  }
}
const SLOW_AUDIO_RATE = 0.6;
const NORMAL_AUDIO_RATE = 1;
const REJECT_ERRORS = ['notsupportederror', 'notallowederror'];
let AudioPlayerService = /*#__PURE__*/(() => {
  class AudioPlayerService {
    constructor(upConfig, injector, audioLoader, b4xLoader, zone) {
      this.upConfig = upConfig;
      this.injector = injector;
      this.audioLoader = audioLoader;
      this.b4xLoader = b4xLoader;
      this.zone = zone;
      this.durationHash = {};
      this.volumeChange = new EventEmitter();
      this._muted = false;
      this._volume = 0.9;
      this.hasOggSupport = false;
      this.pcm = new Float32Array(0);
      this.wavHash = {};
      this.audio = new Audio();
      this.init();
    }
    set currentTime(value) {
      if (value === this.audio.duration || value === 0) {
        value = 0.01;
      }
      try {
        this.audio.currentTime = value;
      } catch (e) {}
    }
    get currentTime() {
      if (this.audio) {
        return this.audio.currentTime || 0;
      }
      return 0;
    }
    get duration() {
      if (this.audio) {
        return this.audio.duration || 0;
      }
      return 0;
    }
    /**
     * Mute an unmute audio
     */
    set muted(value) {
      if (this._muted !== value) {
        this.audio.muted = value;
        this._muted = value;
        if (this.audioPool && this.audioPool.length) {
          this.audioPool.forEach(audio => audio.muted = value);
        }
        this.volumeChange.emit({
          volume: this._volume,
          muted: this._muted
        });
      }
    }
    get muted() {
      if (this._muted !== this.audio.muted) {
        this._muted = this.audio.muted;
      }
      return this._muted;
    }
    /**
     * Sets the volume level. Allowed values  0 .. 1
     */
    set volume(value) {
      value = Math.max(0, Math.min(1, value));
      if (this._volume !== value) {
        this._volume = value;
        this.audio.volume = value;
        if (this.audioPool && this.audioPool.length) {
          this.audioPool.forEach(audio => audio.volume = value);
        }
        this.volumeChange.emit({
          volume: this._volume,
          muted: this._muted
        });
      }
    }
    get volume() {
      const value = this.audio.volume;
      if (this._volume !== value) {
        this._volume = value;
      }
      return this._volume;
    }
    clean() {
      this.stop();
      this.resetPcmIfNeeded();
      this.resetEvents();
      if (this.nativeCallback) {
        this.nativeCallback.clean();
      }
      if (this.nativePlayer && typeof this.nativePlayer.setCallback === 'function') {
        this.nativePlayer.setCallback(null);
      }
      if (this.durationsDb) {
        this.durationsDb.clean();
      }
      this.audio = null;
      this.audioPool = null;
      this.durationHash = null;
      this.durationsDb = null;
      this.volumeChange = null;
      this.nativeCallback = null;
      this.nativePlayer = null;
      this.pcm = null;
      this.urlQueue = null;
      this.wavHash = null;
    }
    getAudioDurationFromHash() {
      if (this.audio && this.durationHash) {
        const url = this.audio.src || '';
        const keys = Object.keys(this.durationHash);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          if (url.indexOf(key) !== -1) {
            return this.durationHash[key].duration;
          }
        }
        return this.audio.duration;
      }
      return 0;
    }
    /**
     * Load and return audio duration
     */
    getAudioDuration(url, isSlowPlaying = false) {
      if (!url) {
        return of({
          duration: 0,
          url
        });
      }
      let obs = this.normalizeUrl(url).pipe(map(normalizedUrl => {
        url = normalizedUrl;
        return url;
      }));
      if (this.hasOggSupport && isOgg$1(url)) {
        obs = this.loadWAV(url);
      }
      return obs.pipe(mergeMap(str => {
        if (this.durationHash[str]) {
          return of(this.durationHash[str]);
        }
        return this.durationsDb.read(url).pipe(mergeMap(dur => {
          if (dur) {
            return of(parseFloat(dur));
          }
          return new Observable(observer => {
            let retryCounter = 0;
            let sub;
            let audio;
            // When network connection was lost audio duration will be "Infinity" or "NaN"
            const retryGetAudioDuration = () => {
              audio = new Audio();
              audio.autoplay = false;
              audio.preload = 'metadata';
              audio.loop = false;
              audio.ondurationchange = () => {
                audio.ondurationchange = null;
                audio.onerror = null;
                sub = cleanSubscription(sub);
                if ((audio.duration === Infinity || isNaN(audio.duration)) && retryCounter < 5) {
                  audio = null;
                  retryCounter++;
                  sub = timer(100).subscribe(() => retryGetAudioDuration());
                } else {
                  const audioDuration = audio.duration === Infinity || isNaN(audio.duration) ? 0 : audio.duration;
                  if (audioDuration) {
                    sub = this.durationsDb.put(url, String(audioDuration)).subscribe(() => '');
                  }
                  observer.next(audioDuration);
                  observer.complete();
                  audio = null;
                }
              };
              audio.onerror = () => {
                audio.ondurationchange = null;
                audio.onerror = null;
                observer.next(0);
                observer.complete();
                audio = null;
              };
              audio.src = str;
            };
            retryGetAudioDuration();
            return () => {
              if (audio) {
                audio.ondurationchange = null;
                audio.onerror = null;
              }
              audio = null;
              sub = cleanSubscription(sub);
            };
          });
        }), map(duration => {
          const obj = {
            duration,
            url: str
          };
          if (duration > 0) {
            this.durationHash[obj.url] = obj;
          }
          return obj;
        }));
      }));
    }
    /**
     * Loads sound and extracts pcm data
     */
    load(url) {
      const obs = this.hasOggSupport && isOgg$1(url) ? this.loadWAV(url) : this.normalizeUrl(url);
      return obs.pipe(mergeMap(normalizedUrl => this.audioLoader.load(normalizedUrl)), tap(audioData => {
        const obj = {
          duration: audioData.duration,
          url: audioData.url
        };
        if (obj.duration > 0) {
          this.durationHash[obj.url] = obj;
        }
      }));
    }
    /**
     * Plays given audio url
     */
    play(url, isSlowPlaying = false) {
      if (!url) {
        return throwError('Url must be defined!');
      }
      if (this.hasOggSupport && isOgg$1(url)) {
        return this.loadWAV(url).pipe(mergeMap(wavUrl => this.play(wavUrl, isSlowPlaying)));
      }
      const audioRate = isSlowPlaying ? SLOW_AUDIO_RATE : NORMAL_AUDIO_RATE;
      let inUrl = '';
      return timer(0).pipe(mergeMap(() => this.normalizeUrl(url)), mergeMap(normalizedUrl => {
        this.pause();
        this.resetPcmIfNeeded();
        url = normalizedUrl || '';
        inUrl = url;
        const urlObj = createUrlObject(inUrl);
        let sourceElement = Array.from(this.audio.children).find(child => child.tagName.toLowerCase() === 'source');
        if (sourceElement && !sourceElement.src?.includes(url) || !this.audio?.src?.includes(url)) {
          if (sourceElement) {
            this.audio.removeChild(sourceElement);
          }
          if (CWDetection.isSafari && url.startsWith('blob:') && CWDetection.IOSVersion === 13) {
            this.audio.removeAttribute('src');
            sourceElement = document.createElement('source');
            this.audio.appendChild(sourceElement);
            sourceElement.src = url;
          } else {
            if (CWDetection.isSafari && !CWDetection.isModernSafari) {
              urlObj.searchParams.append('cacheBust', '' + Date.now());
            }
            this.audio.src = urlObj.href;
          }
        } else if (CWDetection.isWebApp && CWDetection.isIOS && !CWDetection.isModernIOS && (sourceElement && sourceElement.src?.includes(url) || this.audio?.src?.includes(url))) {
          if (!inUrl.startsWith('blob:')) {
            urlObj.searchParams.append('cacheBust', '' + Date.now());
          }
          this.audio.src = urlObj.href;
        }
        this.audio.volume = this.volume;
        return from((this.audio.play() || Promise.resolve(true)).catch(e => {
          if (e && typeof e.toString === 'function' && e.toString() === '[object DOMError]') {
            return this.audio.play();
          } else if (e && REJECT_ERRORS.includes((e.name || '').toLowerCase())) {
            return Promise.reject(e);
          }
          console.log(e);
          return Promise.resolve();
        }));
      }), mergeMap(() => new Observable(observer => {
        this.changePlaybackRate(audioRate);
        this.resetEvents();
        observer.next({
          type: 'playing'
        });
        this.audio.ondurationchange = evt => {
          const obj = {
            duration: this.audio.duration,
            url: inUrl
          };
          if (obj.duration > 0) {
            this.durationHash[obj.url] = obj;
          }
          observer.next(evt);
        };
        this.audio.onerror = err => {
          console.log(err);
          observer.complete();
        };
        this.audio.onended = observer.complete.bind(observer);
        this.audio.onpause = observer.complete.bind(observer);
        return () => {
          this.audio.pause();
          this.resetEvents();
        };
      })), catchError(e => {
        return new Observable(observer => {
          observer.error(e);
          observer.complete();
        });
      }));
    }
    playBunch(url, urls = null) {
      if (!url) {
        return throwError('Url must be defined!');
      }
      this.pause();
      if (this.hasOggSupport && isOgg$1(url)) {
        return this.loadWAV(url).pipe(mergeMap(wavUrl => this.playBunch(wavUrl, urls)));
      }
      if (urls && urls.length) {
        urls.splice(urls.indexOf(url), 1);
        this.urlQueue = urls;
        const len = Math.min(urls.length, this.audioPool.length);
        for (let i = 1; i < len; i++) {
          this.preloadUrl(this.audioPool[i], this.urlQueue.shift());
        }
        this.audio = this.audioPool[0];
      } else {
        this.audio = this.audioPool.find(audioElem => audioElem.src?.includes(url)) || this.audioPool[0];
      }
      return new Observable(observer => {
        if (!this.audio?.src?.includes(url)) {
          this.audio.src = url;
        } else if (CWDetection.isWebApp && CWDetection.isIOS && !CWDetection.isModernIOS && this.audio?.src?.includes(url)) {
          const urlObj = createUrlObject(url);
          if (!url.startsWith('blob:')) {
            urlObj.searchParams.append('cacheBust', '' + Date.now());
          }
          this.audio.src = urlObj.href;
        }
        this.audio.volume = this._volume;
        (this.audio.play() || Promise.resolve(true)).catch(e => {
          if (e && typeof e.toString === 'function' && e.toString() === '[object DOMError]') {
            return this.audio.play();
          } else if (e && REJECT_ERRORS.includes((e.name || '').toLowerCase())) {
            observer.error(e);
            return Promise.reject(e);
          }
          console.log(e);
          return Promise.resolve();
        }).catch(err => {
          console.log(err);
          observer.complete();
        }).then(() => {
          this.changePlaybackRate(NORMAL_AUDIO_RATE);
          this.resetEvents();
          observer.next({
            type: 'playing'
          });
          this.audio.ondurationchange = evt => {
            const obj = {
              duration: this.audio.duration,
              url
            };
            if (obj.duration > 0) {
              this.durationHash[obj.url] = obj;
            }
            observer.next(evt);
          };
          this.audio.onerror = err => {
            console.log(err);
            observer.complete();
          };
          this.audio.onended = () => {
            observer.complete();
            this.preloadUrl(this.audio, (this.urlQueue || []).shift());
          };
          this.audio.onpause = () => {
            observer.complete();
            if (this.audio.currentTime === this.audio.duration) {
              this.preloadUrl(this.audio, (this.urlQueue || []).shift());
            }
          };
        });
        return () => {
          this.audio.pause();
          this.resetEvents();
        };
      });
    }
    /**
     * Converts pcm data to base64 or blob string and plays it
     */
    playPcmData(pcm) {
      const audioRate = NORMAL_AUDIO_RATE;
      return timer(0).pipe(mergeMap(() => {
        this.pause();
        const url = this.resetPcmIfNeeded(pcm);
        if (url) {
          this.audio.src = this.objectUrl;
        }
        this.audio.volume = this.volume;
        return from((this.audio.play() || Promise.resolve(true)).catch(e => {
          if (e && typeof e.toString === 'function' && e.toString() === '[object DOMError]') {
            return this.audio.play();
          }
          console.log(e);
          return Promise.resolve();
        }));
      }), mergeMap(() => new Observable(observer => {
        this.changePlaybackRate(audioRate);
        this.resetEvents();
        observer.next({
          type: 'playing'
        });
        this.audio.ondurationchange = observer.next.bind(observer);
        this.audio.onerror = err => {
          console.log(err);
          observer.complete();
        };
        this.audio.onended = observer.complete.bind(observer);
        this.audio.onpause = observer.complete.bind(observer);
        return () => {
          this.audio.pause();
          this.resetEvents();
        };
      })));
    }
    /**
     * Plays audio portion with given start and end positions
     */
    playUrlSelection(url, start, end, isSlowPlaying = false) {
      if (this.hasOggSupport && isOgg$1(url)) {
        return this.loadWAV(url).pipe(mergeMap(wavUrl => this.playUrlSelection(wavUrl, start, end)));
      }
      let observer;
      if (this.rangeIntervalId) {
        clearInterval(this.rangeIntervalId);
      }
      const onRangeUpdate = () => {
        if (this.audio.currentTime >= end) {
          clearInterval(this.rangeIntervalId);
          this.rangeIntervalId = null;
          this.stop();
          observer.complete();
        }
      };
      const audioRate = isSlowPlaying ? SLOW_AUDIO_RATE : NORMAL_AUDIO_RATE;
      return timer(0).pipe(mergeMap(() => this.normalizeUrl(url)), mergeMap(normalizedUrl => {
        this.pause();
        this.resetPcmIfNeeded();
        url = normalizedUrl || '';
        const inUrl = url;
        let sourceElement = Array.from(this.audio.children).find(child => child.tagName.toLowerCase() === 'source');
        if (sourceElement && !sourceElement.src?.includes(url) || !this.audio?.src?.includes(url)) {
          if (sourceElement) {
            this.audio.removeChild(sourceElement);
          }
          if (CWDetection.isSafari && url.startsWith('blob:') && CWDetection.IOSVersion === 13) {
            this.audio.removeAttribute('src');
            sourceElement = document.createElement('source');
            this.audio.appendChild(sourceElement);
            sourceElement.src = url;
          } else {
            this.audio.src = inUrl;
          }
        }
        this.audio.volume = this.volume;
        return from((this.audio.play() || Promise.resolve(true)).catch(e => {
          if (e && typeof e.toString === 'function' && e.toString() === '[object DOMError]') {
            return this.audio.play();
          }
          console.log(e);
          return Promise.resolve();
        }));
      }), mergeMap(() => new Observable(obs => {
        this.changePlaybackRate(audioRate);
        this.resetEvents();
        if (this.audio.currentTime < start || this.audio.currentTime >= end) {
          this.currentTime = start;
        }
        observer = obs;
        observer.next({
          type: 'playing'
        });
        this.audio.ondurationchange = evt => {
          const obj = {
            duration: this.audio.duration,
            url
          };
          if (obj.duration > 0) {
            this.durationHash[obj.url] = obj;
          }
          observer.next(evt);
        };
        this.audio.onerror = err => {
          console.log(err);
          observer.complete();
        };
        this.audio.onended = observer.complete.bind(observer);
        this.audio.onpause = observer.complete.bind(observer);
        this.rangeIntervalId = setInterval(onRangeUpdate, 60);
        return () => {
          this.audio.pause();
          this.resetEvents();
        };
      }).pipe(map(evt => {
        if (evt.type === 'durationchange') {
          if (this.currentTime < start) {
            this.currentTime = start;
          }
        }
        return evt;
      }))));
    }
    /**
     * Converts pcm data to base64 or blob string and plays it
     */
    playPcmDataSelection(pcmData, start, end) {
      let observer;
      const onRangeUpdate = () => {
        if (this.audio.currentTime >= end) {
          clearInterval(this.rangeIntervalId);
          this.rangeIntervalId = null;
          this.stop();
          observer.complete();
        }
      };
      return timer(0).pipe(mergeMap(() => {
        this.pause();
        const url = this.resetPcmIfNeeded(pcmData);
        if (url) {
          this.audio.src = this.objectUrl;
        }
        this.audio.volume = this.volume;
        return from((this.audio.play() || Promise.resolve(true)).catch(e => {
          if (e && typeof e.toString === 'function' && e.toString() === '[object DOMError]') {
            return this.audio.play();
          }
          console.log(e);
          return Promise.resolve();
        }));
      }), mergeMap(() => new Observable(obs => {
        this.changePlaybackRate(NORMAL_AUDIO_RATE);
        this.resetEvents();
        if (this.audio.currentTime < start || this.audio.currentTime >= end) {
          this.currentTime = start;
        }
        observer = obs;
        observer.next({
          type: 'playing'
        });
        this.audio.ondurationchange = observer.next.bind(observer);
        this.audio.onerror = err => {
          console.log(err);
          observer.complete();
        };
        this.audio.onended = observer.complete.bind(observer);
        this.audio.onpause = observer.complete.bind(observer);
        this.rangeIntervalId = setInterval(onRangeUpdate, 60);
        return () => {
          this.audio.pause();
          this.resetEvents();
        };
      }).pipe(map(evt => {
        if (evt.type === 'durationchange') {
          if (this.currentTime < start) {
            this.currentTime = start;
          }
        }
        return evt;
      }))));
    }
    /**
     * Pause playing audio
     */
    pause() {
      if (this.audio) {
        try {
          this.audio.pause();
        } catch (e) {}
        this.audio.dispatchEvent(createPauseEvent()); // to complete subscription
      }
    }
    /**
     * Stop playing audio and move its time to start
     */
    stop() {
      if (this.audio) {
        this.pause();
        try {
          if (CWDetection.isFirefox) {
            this.audio.load();
          } else {
            this.audio.currentTime = 0;
          }
        } catch (e) {
          this.audio.load();
        }
        this.audio.dispatchEvent(createPauseEvent()); // to complete subscription
      }
    }
    /**
     * Initializes native player
     */
    init() {
      const win = window;
      this.nativePlayer = CWDetection.getIsNative(win) ? win.upAudioPlayer : null;
      this.audio = getAudioPlayer(window) || new Audio();
      const audioPools = getTopWindow(window).audioPool || [this.audio];
      this.audioPool = CWDetection.isAndroid ? audioPools.slice(0, 1) : audioPools;
      if (this.nativePlayer) {
        this.hasOggSupport = true;
        this.nativeCallback = new NativeCallback(this.zone);
        this.nativePlayer.setCallback(this.nativeCallback.onNativeCallback.bind(this.nativeCallback));
      }
      this.durationsDb = new IdxDbStoreTable('durations', 'cw-audio-durations');
      // need remove after next release https://jira.transparent.com/browse/HTML-16164
      if (typeof window.caches !== 'undefined') {
        caches.delete('cw-audio-durations').catch(() => '');
      }
    }
    preloadUrl(audio, url) {
      /* eslint-disable prefer-const */
      if (url && audio) {
        let obs = of(url);
        let sub;
        if (this.hasOggSupport && isOgg$1(url)) {
          obs = this.loadWAV(url);
        }
        sub = obs.subscribe(() => {
          audio.src = url;
          audio.load();
          cleanSubscription(sub);
        });
      }
      /* eslint-disable prefer-const */
    }
    /**
     * Reset object url and set pcm data
     */
    resetPcmIfNeeded(pcmData) {
      let url;
      if (!pcmData || this.pcm && !compareFloatArrays(this.pcm, pcmData)) {
        if (this.objectUrl) {
          revokeObjectURL(this.objectUrl);
        }
        this.objectUrl = null;
      }
      if (pcmData && !this.objectUrl) {
        this.pcm = pcmData;
        url = this.objectUrl = createUrlFromPCM(pcmData);
      }
      return url;
    }
    changePlaybackRate(audioRate) {
      if (this.audio.playbackRate !== audioRate) {
        this.audio.playbackRate = audioRate;
      }
      if (this.audio.defaultPlaybackRate !== audioRate) {
        this.audio.defaultPlaybackRate = audioRate;
      }
    }
    /**
     * Convert ogg file to wav
     */
    loadWAV(url = '') {
      if (!url) {
        return throwError(new Error('url is empty'));
      }
      if (this.wavHash[url]) {
        return of(this.wavHash[url]);
      }
      return new Observable(subscriber => {
        this.nativeCallback.onErrorChange = err => {
          this.nativeCallback.onErrorChange = null;
          this.nativeCallback.onSoundDataLoadCompleteChange = null;
          subscriber.error(err);
          subscriber.complete();
        };
        this.nativeCallback.onSoundDataLoadCompleteChange = data => {
          if (data) {
            this.wavHash[url] = data;
          }
          this.nativeCallback.onErrorChange = null;
          this.nativeCallback.onSoundDataLoadCompleteChange = null;
          subscriber.next(data);
          subscriber.complete();
        };
        this.nativePlayer.getWAV(url);
        return () => {
          this.nativeCallback.onErrorChange = null;
          this.nativeCallback.onSoundDataLoadCompleteChange = null;
        };
      });
    }
    resetEvents() {
      if (this.rangeIntervalId) {
        clearInterval(this.rangeIntervalId);
        this.rangeIntervalId = null;
      }
      if (this.audio) {
        this.audio.oncanplay = null;
        this.audio.ondurationchange = null;
        this.audio.onended = null;
        this.audio.onerror = null;
        this.audio.onpause = null;
        this.audio.onplaying = null;
        this.audio.onratechange = null;
      }
    }
    normalizeUrl(url) {
      if (this._url !== url) {
        this.b4xLoader.removeUrlFromMemory(this.normalizedUrl);
        this._url = url || '';
        return this.b4xLoader.getUrlFromZip(this._url).pipe(map(str => {
          this.normalizedUrl = str;
          return this.normalizedUrl;
        }));
      }
      return of(this.normalizedUrl);
    }
    static {
      this.ɵfac = function AudioPlayerService_Factory(t) {
        return new (t || AudioPlayerService)(i0.ɵɵinject(PLAYER_CONFIG), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(AudioLoaderService), i0.ɵɵinject(i2.B4xLoaderService), i0.ɵɵinject(i0.NgZone));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AudioPlayerService,
        factory: AudioPlayerService.ɵfac
      });
    }
  }
  return AudioPlayerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function createUrlObject(url) {
  url = url || '';
  if (url.startsWith('//')) {
    url = window.location.protocol + url;
  }
  let objUrl;
  try {
    objUrl = new URL(url);
  } catch (e) {
    objUrl = {
      href: url,
      searchParams: {
        append: (name, value) => {}
      }
    };
  }
  return objUrl;
}
function getScript() {
  const file = new Blob([`class WorkletProcessor extends AudioWorkletProcessor {
    retainAudioBuffer = false;
    buffers = [];
    samplesLength = 0;
    isFirstBufferSent = false;

    constructor() {
        super();
        this.port.onmessage = this.handleMessage.bind(this);
    }

    handleMessage(event) {
        const message = event.data;
        if (message.type === 'retainBuffer') {
            this.retainAudioBuffer = message.retainBuffer;
        } else if (message.type === 'getBuffers') {
            this.retainAudioBuffer = false;
            this.isFirstBufferSent = false;
            this.port.postMessage({type: 'buffers', buffers: this.mergeBuffers()});
            this.buffers.length = 0;
            this.samplesLength = 0;
        }
    }

    mergeBuffers() {
        const result = new Float32Array(this.samplesLength);
        let offset = 0;
        this.buffers.forEach((buffer) => {
            result.set(buffer, offset);
            offset += buffer.length;
        });
        return result;
    }

    process(inputs) {
        if (this.retainAudioBuffer) {
            // The processor may have multiple inputs and outputs. Get the first input and
            const inputBuffer = inputs[0][0] || [];
            if (inputBuffer.length) {
                this.buffers.push(inputBuffer.slice(0));
                this.samplesLength += inputBuffer.length;
                if (!this.isFirstBufferSent && this.samplesLength >= 4096) {
                    this.isFirstBufferSent = true;
                    this.port.postMessage({type: 'firstBuffer', buffers: inputBuffer.slice(0)});
                }
            }
            return true;
        }
        return true;
    }
}

registerProcessor("processor-script", WorkletProcessor);
`], {
    type: 'application/javascript'
  });
  return URL.createObjectURL(file);
}
class IWasCorrectRequestDTO {
  constructor() {
    this.ticket = '';
    this.languageLearn = '';
    this.languageNative = '';
    this.mode = '';
  }
}

/* eslint-disable */
let AMF = /*#__PURE__*/(() => {
  class AMF {
    static {
      this.EMPTY_STRING = '';
    }
    static {
      this.NULL_STRING = 'null';
    }
    static {
      this.NULL_TYPE = 1;
    }
    static {
      this.FALSE_TYPE = 2;
    }
    static {
      this.TRUE_TYPE = 3;
    }
    static {
      this.INTEGER_TYPE = 4;
    }
    static {
      this.DOUBLE_TYPE = 5;
    }
    static {
      this.STRING_TYPE = 6;
    }
    static {
      this.XML_TYPE = 7;
    }
    static {
      this.DATE_TYPE = 8;
    }
    static {
      this.ARRAY_TYPE = 9;
    }
    static {
      this.OBJECT_TYPE = 10;
    }
    static {
      this.BYTEARRAY_TYPE = 12;
    }
    static {
      this.AMF0_AMF3 = 17;
    }
    static {
      this.UINT29_MASK = 536870911;
    }
    static {
      this.INT28_MAX_VALUE = 268435455;
    }
    static {
      this.INT28_MIN_VALUE = -268435456;
    }
    static {
      this.CLASS_ALIAS = '_explicitType';
    }
    static transform(data) {
      const serializer = new Serializer();
      serializer.writeObject(data);
      const arr = new Uint8Array(serializer.writer.data);
      serializer.writer.clean();
      return arr;
    }
    static uuid(a, b) {
      if (a) {
        return (b | Math.random() * 16 >> b / 4).toString(16);
      } else {
        const pat = new RegExp('1|0|(8)', 'g');
        return (1e7 + -1e3 + -4e3 + -8e3 + -1e11 + '').replace(pat, this.uuid);
      }
    }
  }
  return AMF;
})();
class Serializer {
  constructor() {
    this.writer = new Writer();
  }
  writeObject(object) {
    this.writer.writeObject(object);
  }
  writeHeader(header) {
    this.writer.writeUTF(header.name);
    this.writer.writeBoolean(header.mustUnderstand);
    this.writer.writeInt(1);
    this.writer.reset();
    this.writer.write(1);
    this.writer.writeBoolean(true);
  }
  writeBody(body) {
    if (body.targetURI == null) {
      this.writer.writeUTF(AMF.NULL_STRING);
    } else {
      this.writer.writeUTF(body.targetURI);
    }
    if (body.responseURI == null) {
      this.writer.writeUTF(AMF.NULL_STRING);
    } else {
      this.writer.writeUTF(body.responseURI);
    }
    this.writer.writeInt(1);
    this.writer.reset();
    this.writer.write(AMF.AMF0_AMF3);
    this.writeObject(body.data);
  }
}
/**
 *
 */
class Writer {
  constructor() {
    this.data = [];
    this.objects = [];
    this.traits = {};
    this.strings = {};
    this.stringCount = 0;
    this.traitCount = 0;
    this.objectCount = 0;
  }
  clean() {
    this.data.length = 0;
    this.objects.length = 0;
    this.traits = {};
    this.strings = {};
    this.stringCount = 0;
    this.traitCount = 0;
    this.objectCount = 0;
  }
  write(v) {
    this.data.push(v);
  }
  writeShort(v) {
    this.write(v >>> 8 & 255);
    this.write(v >>> 0 & 255);
  }
  writeUTF(v, asAmf) {
    let bytearr, c, i, strlen, utflen;
    strlen = v.length;
    utflen = 0;
    for (i = 0; i < strlen; i++) {
      c = v.charCodeAt(i);
      if (c > 0 && c < 128) {
        utflen++;
      } else if (c > 2047) {
        utflen += 3;
      } else {
        utflen += 2;
      }
    }
    bytearr = [];
    if (asAmf) {
      this.writeUInt29(utflen << 1 | 1);
    } else {
      bytearr.push(utflen >>> 8 & 255);
      bytearr.push(utflen & 255);
    }
    for (i = 0; i < strlen; i++) {
      c = v.charCodeAt(i);
      if (c > 0 && c < 128) {
        bytearr.push(c);
      } else if (c > 2047) {
        bytearr.push(224 | c >> 12);
        bytearr.push(128 | c >> 6 & 63);
        if (asAmf) {
          bytearr.push(128 | c >> 0 & 63);
        } else {
          bytearr.push(128 | c & 63);
        }
      } else {
        bytearr.push(192 | c >> 6);
        if (asAmf) {
          bytearr.push(128 | c >> 0 & 63);
        } else {
          bytearr.push(128 | c & 63);
        }
      }
    }
    this.writeAll(bytearr);
    return asAmf ? utflen : utflen + 2;
  }
  writeUInt29(v) {
    if (v < 128) {
      this.write(v);
    } else if (v < 16384) {
      this.write(v >> 7 & 127 | 128);
      this.write(v & 127);
    } else if (v < 2097152) {
      this.write(v >> 14 & 127 | 128);
      this.write(v >> 7 & 127 | 128);
      this.write(v & 127);
    } else if (v < 0x40000000) {
      this.write(v >> 22 & 127 | 128);
      this.write(v >> 15 & 127 | 128);
      this.write(v >> 8 & 127 | 128);
      this.write(v & 255);
    } else {
      throw new Error('Integer out of range: ' + v);
    }
  }
  /**
   * Write given list of numbers to amf3 stream
   * @param bytes List of binary numbers
   */
  writeAll(bytes) {
    const len = bytes.length;
    let writeLoopCount = 1,
      writePosition = 0,
      currentMaxLength = 50000;
    const maxPartLength = 50000;
    if (len > maxPartLength) {
      writeLoopCount = Math.ceil(len / maxPartLength);
    } else {
      currentMaxLength = len;
    }
    const writeParts = () => {
      if (writeLoopCount !== 0) {
        for (let i = writePosition; i < currentMaxLength; i++) {
          this.write(bytes[i]);
        }
        writeLoopCount--;
        writePosition += maxPartLength;
        currentMaxLength = writePosition + maxPartLength > len ? len : currentMaxLength + maxPartLength;
        writeParts();
      }
    };
    writeParts();
  }
  writeBoolean(v) {
    this.write(v ? 1 : 0);
  }
  /**
   *
   */
  writeInt(v) {
    this.write(v >>> 24 & 255);
    this.write(v >>> 16 & 255);
    this.write(v >>> 8 & 255);
    this.write(v >>> 0 & 255);
  }
  /**
   *
   */
  writeUnsignedInt(v) {
    v < 0 && (v = -(v ^ 4294967295) - 1);
    v &= 4294967295;
    this.write(v >> 24 & 255);
    this.write(v >> 16 & 255);
    this.write(v >> 8 & 255);
    this.write(v & 255);
  }
  /**
   *
   */
  _getDouble(v) {
    const r = [0, 0];
    if (v !== v) {
      r[0] = -524288;
      return r;
    }
    const d = v < 0 || v === 0 && 1 / v < 0 ? -2147483648 : 0;
    v = Math.abs(v);
    if (v === Number.POSITIVE_INFINITY) {
      r[0] = d | 2146435072;
      return r;
    }
    let e = 0;
    for (e = 0; v >= 2 && e <= 1023;) {
      e++;
      v /= 2;
    }
    for (; v < 1 && e >= -1022;) {
      e--;
      v *= 2;
    }
    e += 1023;
    if (e === 2047) {
      return r[0] = d | 2146435072, r;
    }
    let i = 0;
    e === 0 ? (i = v * Math.pow(2, 23) / 2, v = Math.round(v * Math.pow(2, 52) / 2)) : (i = v * Math.pow(2, 20) - Math.pow(2, 20), v = Math.round(v * Math.pow(2, 52) - Math.pow(2, 52)));
    r[0] = d | e << 20 & 2147418112 | i & 1048575;
    r[1] = v;
    return r;
  }
  /**
   *
   */
  writeDouble(v) {
    const arr = this._getDouble(v);
    this.writeUnsignedInt(arr[0]);
    this.writeUnsignedInt(arr[1]);
  }
  /**
   *
   */
  reset() {
    this.objects = [];
    this.objectCount = 0;
    this.traits = {};
    this.traitCount = 0;
    this.strings = {};
    this.stringCount = 0;
  }
  /**
   *
   */
  writeStringWithoutType(v) {
    if (v.length === 0) {
      this.writeUInt29(1);
    } else {
      if (!this.stringByReference(v)) {
        this.writeUTF(v, true);
      }
    }
  }
  /**
   *
   */
  stringByReference(v) {
    const ref = this.strings[v];
    if (ref) {
      this.writeUInt29(ref << 1);
    } else {
      this.strings[v] = this.stringCount++;
    }
    return ref;
  }
  /**
   *
   */
  objectByReference(v) {
    let ref = 0;
    let found = false;
    for (; ref < this.objects.length; ref++) {
      if (this.objects[ref] === v) {
        found = true;
        break;
      }
    }
    if (found) {
      this.writeUInt29(ref << 1);
    } else {
      this.objects.push(v);
      this.objectCount++;
    }
    return found ? ref : null;
  }
  /**
   *
   */
  traitsByReference(v, alias) {
    let s = alias + '|';
    for (let i = 0; i < v.length; i++) {
      s += v[i] + '|';
    }
    const ref = this.traits[s];
    if (ref) {
      this.writeUInt29(ref << 2 | 1);
    } else {
      this.traits[s] = this.traitCount++;
    }
    return ref;
  }
  /**
   *
   */
  writeAmfInt(v) {
    if (v >= AMF.INT28_MIN_VALUE && v <= AMF.INT28_MAX_VALUE) {
      v = v & AMF.UINT29_MASK;
      this.write(AMF.INTEGER_TYPE);
      this.writeUInt29(v);
    } else {
      this.write(AMF.DOUBLE_TYPE);
      this.writeDouble(v);
    }
  }
  /**
   */
  writeDate(v) {
    this.write(AMF.DATE_TYPE);
    if (!this.objectByReference(v)) {
      this.writeUInt29(1);
      this.writeDouble(v.getTime());
    }
  }
  /**
   */
  writeObject(v) {
    if (v == null) {
      this.write(AMF.NULL_TYPE);
      return;
    }
    if (v.constructor === String) {
      this.write(AMF.STRING_TYPE);
      this.writeStringWithoutType(v);
    } else if (v.constructor === Number) {
      if (v === +v && v === (v | 0)) {
        this.writeAmfInt(v);
      } else {
        this.write(AMF.DOUBLE_TYPE);
        this.writeDouble(v);
      }
    } else if (v.constructor === Boolean) {
      this.write(v ? AMF.TRUE_TYPE : AMF.FALSE_TYPE);
    } else if (v.constructor === Date) {
      this.writeDate(v);
    } else {
      if (v.constructor === Array) {
        this.writeArray(v);
      } else if (AMF.CLASS_ALIAS in v) {
        this.writeCustomObject(v);
      } else {
        this.writeMap(v);
      }
    }
  }
  /**
   */
  writeCustomObject(v) {
    if (v._explicitType === 'ByteArray') {
      this.write(AMF.BYTEARRAY_TYPE);
      v = v.data;
      this.writeUInt29(v.length << 1 | 1);
      this.writeAll(v);
    } else {
      this.write(AMF.OBJECT_TYPE);
      if (!this.objectByReference(v)) {
        const traits = this.writeTraits(v);
        for (let i = 0; i < traits.length; i++) {
          const prop = traits[i];
          this.writeObject(v[prop]);
        }
      }
    }
  }
  writeTraits(v) {
    const traits = [];
    let count = 0;
    const externalizable = false;
    const dynamic = false;
    for (const t in v) {
      if (t !== AMF.CLASS_ALIAS) {
        traits.push(t);
        count++;
      }
    }
    if (!this.traitsByReference(traits, v[AMF.CLASS_ALIAS])) {
      this.writeUInt29(3 | (externalizable ? 4 : 0) | (dynamic ? 8 : 0) | count << 4);
      this.writeStringWithoutType(v[AMF.CLASS_ALIAS]);
      if (count > 0) {
        for (const prop of traits) {
          this.writeStringWithoutType(prop);
        }
      }
    }
    return traits;
  }
  /**
   * Writes map
   */
  writeMap(v) {
    this.write(AMF.OBJECT_TYPE);
    if (!this.objectByReference(v)) {
      this.writeUInt29(11);
      this.traitCount++; //  bogus traits entry here
      this.writeStringWithoutType(AMF.EMPTY_STRING); // class name
      for (let key in v) {
        if (key) {
          this.writeStringWithoutType(key);
        } else {
          this.writeStringWithoutType(AMF.EMPTY_STRING);
        }
        this.writeObject(v[key]);
      }
      this.writeStringWithoutType(AMF.EMPTY_STRING);
    }
  }
  /**
   * Write array
   */
  writeArray(v) {
    this.write(AMF.ARRAY_TYPE);
    if (!this.objectByReference(v)) {
      this.writeUInt29(v.length << 1 | 1);
      this.writeUInt29(1);
      if (v.length > 0) {
        for (let i = 0; i < v.length; i++) {
          this.writeObject(v[i]);
        }
      }
    }
  }
}
class SamplesListDTO {
  constructor() {
    this.listName = '';
    this.languageLearn = '';
    this.languageNative = '';
    this.files = [];
    this.os = navigator.userAgent;
    this.userName = '';
    this.activityType = SCSActivityType.MC2;
  }
}
class FileDTO {
  constructor() {
    this.url = '';
    this.priority = 0;
    this.soundData = '';
    this.hash = '';
    this.phrase1 = '';
    this.phrase2 = '';
    this.phraseTranslit = '';
    this.learningLanguageCode = '';
    this.knownLanguageCode = '';
    this.precompareScore = 0;
    this._explicitType = '';
  }
}
class PreloadRequestDTO {
  constructor() {
    this.files = [];
    this.activityType = SCSActivityType.MC2;
  }
}
const PUNCTUATION = new RegExp('[,;!@#$%^&+=\\s\\|\\{\\}\\[\\]\\(\\)\\?\\+\\*\\.\\\\\\$\\-\\[\\]' + String.fromCharCode(0x200B) + String.fromCharCode(0xff1f) + String.fromCharCode(0x3001) + String.fromCharCode(0x3002) + String.fromCharCode(0x2028) + String.fromCharCode(0x060C) +
// arabic comma
String.fromCharCode(0x061B) +
// arabic semicolon
String.fromCharCode(0x061F) +
// arabic question mark
String.fromCharCode(0x00A0) +
// nbsp; IXT-20265
']|[[:punct:]]|[¿¡،؛؟]'); // ('[\.\\ ]');;
const STRIP_TAGS$2 = /<\/?[^>]+(>|$)/igm;
/**
 * Remove punctuations
 */
function removePunctuations(str) {
  return (str || '').replace(PUNCTUATION, '').replace(STRIP_TAGS$2, '');
}
/**
 * Calculate Distance
 */
function calculateDistance(text, translit) {
  text = removePunctuations(text);
  translit = removePunctuations(translit);
  const textLength = text.length;
  const transLength = translit.length;
  if (textLength === 0) {
    return {
      score: 0,
      textLength,
      transLength
    };
  }
  if (transLength === 0) {
    return {
      score: 0,
      textLength,
      transLength
    };
  }
  let tmp;
  let i;
  let j;
  let prev;
  let val;
  // swap to save some memory O(min(a,b)) instead of O(a)
  if (textLength > transLength) {
    tmp = text;
    text = translit;
    translit = tmp;
  }
  const row = Array(textLength + 1);
  // init the row
  for (i = 0; i <= textLength; i++) {
    row[i] = i;
  }
  // fill in the rest
  for (i = 1; i <= transLength; i++) {
    prev = i;
    for (j = 1; j <= textLength; j++) {
      if (translit[i - 1] === text[j - 1]) {
        val = row[j - 1]; // match
      } else {
        val = Math.min(row[j - 1] + 1,
        // substitution
        Math.min(prev + 1,
        // insertion
        row[j] + 1)); // deletion
      }

      row[j - 1] = prev;
      prev = val;
    }
    row[textLength] = prev;
  }
  return {
    score: row[textLength],
    textLength,
    transLength
  };
}
/**
 * Relative score
 */
function relativeScore(text, translit) {
  const data = calculateDistance(text, translit);
  return data.transLength + data.textLength > 0 ? data.score / (data.transLength + data.textLength) : 0;
}
class CompareRequestDTO {
  constructor() {
    this.ticket = '';
    this.soundData = '';
    this.sounds = [];
    this.user_sound_snr = 0;
    this.correctSoundHash = '';
    this.phrase1 = '';
    this.phrase2 = '';
    this.listName = 'listName';
    this.activityType = SCSActivityType.MC2;
    this.languageLearn = 'languageLearn';
    this.languageNative = 'languageNative';
    this.mode = 'mode';
    this.appName = '';
    this.version = '';
    this.device = '';
    this.gain = 0;
    this.vad_start = 100;
  }
}

/*eslint-disable  no-underscore-dangle*/
const STRIP_TAGS$1 = /<\/?[^>]+(>|$)/igm;
/**
 *
 */
function createPreloadedData(arrayBuffer, fileDTO) {
  const preloadRequestDTO = new PreloadRequestDTO();
  preloadRequestDTO.activityType = fileDTO.activityType;
  delete fileDTO.activityType;
  const byteArray = {};
  byteArray.data = new Uint8Array(arrayBuffer);
  byteArray.length = byteArray.data.length;
  byteArray._explicitType = 'ByteArray';
  fileDTO.soundData = byteArray;
  fileDTO._explicitType = 'com.transparent.jsndcmp.rest.v2.dto.FileDTO';
  preloadRequestDTO.files = [fileDTO];
  preloadRequestDTO._explicitType = 'com.transparent.jsndcmp.rest.v2.dto.PreloadRequestDTO';
  return preloadRequestDTO;
}
/**
 *
 */
function createFileDto(text, cards, scsConfig, hasExplicitType) {
  const files = [];
  const len = cards.length;
  for (let i = 0; i < len; i++) {
    const card = cards[i];
    const fileDTO = new FileDTO();
    fileDTO.hash = card.hash;
    fileDTO.phrase1 = (card.cardSide1Text || '').replace(STRIP_TAGS$1, '');
    fileDTO.phrase2 = (card.cardSide2Text || '').replace(STRIP_TAGS$1, '');
    fileDTO.phraseTranslit = (card.cardTranslit || '').replace(STRIP_TAGS$1, '');
    fileDTO.learningLanguageCode = scsConfig.learningLanguage;
    fileDTO.knownLanguageCode = scsConfig.knownLanguage;
    if (text) {
      fileDTO.precompareScore = relativeScore(text, card.cardTranslit);
    }
    fileDTO.url = card.soundSide2;
    const pat = /^(https?)|(file:\/\/)/;
    if (!pat.test(fileDTO.url)) {
      fileDTO.url = location.protocol + fileDTO.url;
    }
    fileDTO.priority = 0;
    if (hasExplicitType) {
      fileDTO._explicitType = 'com.transparent.jsndcmp.rest.v2.dto.FileDTO';
    } else {
      delete fileDTO._explicitType;
    }
    files.push(fileDTO);
  }
  return files;
}
/**
 *
 */
function createCompareDto(card, userSound, scsConfig) {
  let quality;
  let soundContent;
  const dataView = encodeWAV(userSound, true);
  if (dataView) {
    const obj = {};
    obj.data = new Uint8Array(dataView.buffer);
    obj.length = obj.data.length;
    obj._explicitType = 'ByteArray';
    soundContent = obj;
  }
  const snr = scsConfig.minSnr || 15;
  try {
    quality = UserSoundQuality.check(userSound, snr);
  } catch (error) {
    quality = {
      result: UserSoundQuality.LOW_SNR,
      snr: -1
    };
  }
  const compareData = new CompareRequestDTO();
  compareData.ticket = UIDUtil.createUID();
  compareData.appName = scsConfig.appName;
  compareData.version = '1.0.0';
  compareData.device = '';
  compareData.gain = 1;
  compareData.soundData = soundContent;
  compareData.user_sound_snr = quality.snr;
  compareData.vad_start = 100;
  compareData.activityType = scsConfig.activityType;
  compareData.languageLearn = scsConfig.learningLanguage;
  compareData.languageNative = scsConfig.knownLanguage;
  compareData.listName = scsConfig.listName;
  compareData.mode = scsConfig.mode;
  compareData.phrase1 = (card.cardSide1Text || '').replace(STRIP_TAGS$1, '');
  compareData.phrase2 = (card.cardSide2Text || '').replace(STRIP_TAGS$1, '');
  compareData.correctSoundHash = card.hash;
  const answers = card.answers || [card];
  compareData.sounds = createFileDto(card[scsConfig.side], answers, scsConfig, true);
  compareData._explicitType = 'com.transparent.jsndcmp.rest.v2.dto.CompareRequestDTO';
  return {
    data: compareData,
    snr: quality.result
  };
}
/**
 * Get lsit of unavailable files
 */
function getServerListInfo(cards, scsConfig) {
  const samplesListDTO = new SamplesListDTO();
  samplesListDTO.activityType = scsConfig.activityType;
  samplesListDTO.languageLearn = scsConfig.learningLanguage;
  samplesListDTO.languageNative = scsConfig.knownLanguage;
  samplesListDTO.files = createFileDto('', cards, scsConfig, false);
  samplesListDTO.listName = scsConfig.listName;
  return samplesListDTO;
}
/*eslint-disable  no-underscore-dangle*/

class AimResult {}
function getFromSessionStorage(key) {
  let sessionId = '';
  try {
    if (window.sessionStorage) {
      sessionId = sessionStorage.getItem(key);
      sessionId = (sessionId || '').replace(/"/g, '').trim();
    }
  } catch (e) {}
  return sessionId;
}
function setToSessionStorage(key, data) {
  try {
    if (window.sessionStorage) {
      sessionStorage.setItem(key, data);
    }
  } catch (e) {}
}
const SESSION_STORAGE_KEY = 'scsSession';
const RECONNECT_PERIOD = 10000;
let SCSService = /*#__PURE__*/(() => {
  class SCSService {
    constructor(http, b4xLoader) {
      this.http = http;
      this.b4xLoader = b4xLoader;
      this.isAvailable = true;
      this.hasFreeSlots = true;
      this.apiVersion = 2;
      this.serverUrl = '//sound.transparent.com/jsndcmp/sound/';
      this.serverUrlV2 = '//sound.transparent.com/jsndcmp/v2/sound/';
      setToSessionStorage(SESSION_STORAGE_KEY, '');
    }
    clean() {
      this.config = null;
      this.resolver = null;
    }
    /**
     * Cancel comparison
     */
    cancel(ticket) {
      const body = {
        ticket,
        authenticationTicket: ''
      };
      return this.http.post(`${this.serverUrl}cancel`, body).pipe(catchError(() => of(null)));
    }
    /**
     * This request cancel session of current user.
     */
    cancelSession() {
      setToSessionStorage(SESSION_STORAGE_KEY, '');
      return this.http.get(`${this.serverUrl}cancelsession?${Date.now()}`);
    }
    /**
     * Free a slot when a user completes or navigates away from an activity
     */
    destroySession() {
      const sessionId = getFromSessionStorage(SESSION_STORAGE_KEY);
      setToSessionStorage(SESSION_STORAGE_KEY, '');
      if (!sessionId) {
        return of('');
      }
      return this.http.get(`${this.serverUrlV2}cancelsession/;jsessionid=${sessionId}`);
    }
    /**
     *
     */
    compareAIM(card, userSound, scsConfig) {
      return this.getSessionId().pipe(mergeMap(sessionId => {
        const obj = createCompareDto(card, userSound, scsConfig);
        obj.data.sounds.forEach(fileDTO => fileDTO.url = this.resolveMediaUrl(fileDTO.url));
        const body = AMF.transform(obj.data);
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/x-amf',
            'X-JSESSIONID': sessionId
          }),
          withCredentials: true
        };
        return this.http.post(`${this.serverUrlV2}compare;jsessionid=${sessionId}`, body.buffer, options).pipe(catchError(() => of({})), map(data => {
          const result = this.processAIMResult(data, card);
          result.ticket = obj.data.ticket;
          result.snr = obj.snr;
          result.masterVADResults = data.masterVADResults;
          result.userVadResult = data.userVadResult;
          return result;
        }));
      }));
    }
    /**
     * Request will return part of service settings
     */
    getConfig(url) {
      this.serverUrl = url || this.serverUrl;
      const pattern = `v${this.apiVersion}`;
      if (this.serverUrl.indexOf(pattern) === -1) {
        this.serverUrlV2 = this.serverUrl.replace('jsndcmp/sound', `jsndcmp/v${this.apiVersion}/sound`);
      }
      const options = {
        observe: 'response',
        responseType: 'json',
        withCredentials: true
      };
      return forkJoin([this.callServer(`${this.serverUrl}session/free`).pipe(catchError(() => of({
        body: 0
      })), map(e => {
        this.hasFreeSlots = parseInt(e.body, 10) > 0;
        if (!this.hasFreeSlots) {
          this.isAvailable = false;
        }
        return this.hasFreeSlots;
      })), this.callServer(`${this.serverUrl}config/settings`, 'post', '', options).pipe(map(e => {
        this.setConnectionStatus(e);
        this.config = e.status === 200 ? e.body : {
          minSnr: 15
        };
        return this.config;
      }))]).pipe(map(() => this.config));
    }
    /**
     * Uploads list of sounds to scs server,
     * Get Missing List Files
     * loads and auploads them
     */
    getFiles(cards, scsConfig) {
      let obs = of(null);
      const win = window;
      if (typeof win.CWAPI?.list?.getMediaElementResolver === 'function') {
        obs = from(win.CWAPI.list.getMediaElementResolver());
      }
      return forkJoin([this.getSessionId(), obs]).pipe(mergeMap(arr => {
        const sessionId = arr[0];
        this.resolver = arr[1];
        const samplesListDTO = getServerListInfo(cards, scsConfig);
        samplesListDTO.files.forEach(fileDTO => fileDTO.url = this.resolveMediaUrl(fileDTO.url));
        const options = {
          headers: new HttpHeaders({
            'X-JSESSIONID': sessionId
          }),
          withCredentials: true
        };
        return this.http.post(`${this.serverUrlV2}serverlist;jsessionid=${sessionId}`, samplesListDTO, options).pipe(mergeMap(item => this.uploadSounds(item, cards, scsConfig)));
      }));
    }
    /**
     * A polling method for checking server connectivity
     */
    getVersion() {
      const url = `${this.serverUrl}version/plain`;
      return this.callServer(url).pipe(map(e => this.setConnectionStatus(e)), mergeMap(() => interval(RECONNECT_PERIOD)), switchMap(() => this.callServer(url)), map(res => this.setConnectionStatus(res)));
    }
    /**
     * Sets iwascorrect flag in the log.
     */
    iWasCorrect(ticket, scsOptions, again = false) {
      return this.getSessionId().pipe(mergeMap(sessionId => {
        const iWasCorrectRequestDTO = new IWasCorrectRequestDTO();
        iWasCorrectRequestDTO.ticket = ticket;
        iWasCorrectRequestDTO.languageLearn = scsOptions.learningLanguage;
        iWasCorrectRequestDTO.languageNative = scsOptions.knownLanguage;
        iWasCorrectRequestDTO.mode = scsOptions.mode;
        const options = {
          headers: new HttpHeaders({
            'X-JSESSIONID': sessionId
          }),
          withCredentials: true
        };
        return this.http.post(`${this.serverUrlV2}iwascorrect;jsessionid=${sessionId}`, iWasCorrectRequestDTO, options).pipe(catchError(err => {
          if (err.status === 500 && !again) {
            // refresh expired session id https://jira.transparent.com/browse/HTML-13788
            setToSessionStorage(SESSION_STORAGE_KEY, '');
            return this.iWasCorrect(ticket, scsOptions, true);
          }
          return throwError(err);
        }));
      }));
    }
    /**
     *  Utility method for calling scs server with error
     */
    callServer(url, method = 'get', body, opts) {
      const options = {
        body: 'SCSUnavailable',
        status: 404
      };
      const resp = new HttpResponse(options);
      const timeout = timer(1000).pipe(map(() => resp));
      const scsreq = method === 'get' ? this.http.get(url, {
        observe: 'response',
        responseType: 'text'
      }).pipe(catchError(() => of(resp))) : this.http.post(url, body, opts).pipe(catchError(() => of(resp)));
      return merge(scsreq.pipe(materialize()), timeout.pipe(materialize())).pipe(takeWhile(note => note.hasValue), dematerialize());
    }
    /**
     * Adds sound url to the given user sound key
     */
    normalizeUrl(userSoundKey) {
      return userSoundKey ? `${this.serverUrlV2}usersound/${userSoundKey}` : null;
    }
    /**
     * Check connection status
     */
    setConnectionStatus(res) {
      this.isAvailable = this.hasFreeSlots && res.status < 400;
      this.version = this.isAvailable ? res.body : null;
      return this.isAvailable;
    }
    /**
     * Workaround to get session id
     */
    getSessionId() {
      const sessionId = getFromSessionStorage(SESSION_STORAGE_KEY);
      if (sessionId && sessionId.length > 0) {
        return of(sessionId);
      }
      return this.http.get(`${this.serverUrlV2}session/true`, {
        responseType: 'text',
        withCredentials: true
      }).pipe(map(str => {
        setToSessionStorage(SESSION_STORAGE_KEY, str);
        return str;
      }));
    }
    /**
     * Loads one by one files from source - local or another server and uploads to sound server
     */
    uploadFiles(filesDTO) {
      return this.getSessionId().pipe(mergeMap(sessionId => {
        const observables = [];
        for (const fileDTO of filesDTO) {
          observables.push(this.uploadSingleFile(fileDTO, sessionId));
        }
        return forkJoin(observables);
      }));
    }
    /**
     *  Loads single file  and uploads to sound server
     */
    uploadSingleFile(fileDTO, sessionId) {
      let normalizedUrl = '';
      return this.b4xLoader.getUrlFromZip(fileDTO.url).pipe(mergeMap(str => {
        fileDTO.url = this.resolveMediaUrl(fileDTO.url);
        normalizedUrl = str;
        return this.http.get(`${normalizedUrl}`, {
          responseType: 'arraybuffer'
        });
      }), catchError(() => of(null)), mergeMap(buffer => {
        if (buffer) {
          this.b4xLoader.removeUrlFromMemory(normalizedUrl);
          const preloadRequestDTO = createPreloadedData(buffer, fileDTO);
          const body = AMF.transform(preloadRequestDTO);
          const url = `${this.serverUrlV2}samples/preloadlist;jsessionid=${sessionId}`;
          return this.http.post(url, body.buffer, {
            headers: new HttpHeaders({
              'Content-Type': 'application/x-amf',
              'X-JSESSIONID': sessionId
            }),
            withCredentials: true,
            responseType: 'arraybuffer'
          });
        } else {
          return of(null);
        }
      }));
    }
    /**
     * Upload missing sounds to server
     */
    uploadSounds(data, cards, scsConfig) {
      const res = [];
      const hashes = data.hashes;
      if (!hashes || hashes.length === 0) {
        return of(null);
      }
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        if (hashes.indexOf(card.hash) !== -1) {
          res.push(card);
        }
      }
      const filesDTO = createFileDto('', res, scsConfig, true);
      if (filesDTO.length) {
        return this.uploadFiles(filesDTO);
      }
      return of(null);
    }
    processAIMResult(data, card) {
      let correctAnswerIndex = -1;
      let resultAnswerIndex = -1;
      let sound = 1000000;
      let isCorrect = false;
      let results = data.compareResult || [];
      const answers = card.answers || [card];
      const answersLength = answers.length;
      for (let i = 0; i < answersLength; i++) {
        if (answers[i].isCorrect) {
          correctAnswerIndex = i;
        }
      }
      if (results.length === answersLength) {
        for (let i = 0; i < answersLength; i++) {
          const current = Math.max(0, results[i]);
          if (current < sound) {
            sound = current;
            resultAnswerIndex = i;
          }
        }
        isCorrect = resultAnswerIndex === correctAnswerIndex && sound !== 0;
      }
      const aimValue = +results[0];
      const aimVO = new AimResult();
      aimVO.compareResult = results.slice(0);
      aimVO.isAllMinusOne = results.every(value => +value === -1);
      // Sometimes server may send response scores like [0.99, 0.99, 0.99, 0.99].
      // It means that user is incorrect.
      results = results.filter(result => result !== 0.99);
      aimVO.resultAnswerIndex = resultAnswerIndex;
      aimVO.resultsAreEqual = results.length === 0;
      aimVO.userSoundKey = this.normalizeUrl(data.userSoundKey);
      aimVO.masterMaxSampleIndexes = data.masterMaxSampleIndexes;
      aimVO.userMaxSampleIndex = data.userMaxSampleIndex;
      aimVO.isCorrect = isCorrect;
      aimVO.aimValue = aimValue;
      aimVO.segments = data.segments;
      aimVO.userDetails = data.userDetails;
      aimVO.masterDetails = data.masterDetails;
      aimVO.answerIndex = correctAnswerIndex;
      return aimVO;
    }
    resolveMediaUrl(url) {
      return typeof this.resolver === 'function' ? this.resolver(url) : url;
    }
    static {
      this.ɵfac = function SCSService_Factory(t) {
        return new (t || SCSService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.B4xLoaderService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SCSService,
        factory: SCSService.ɵfac
      });
    }
  }
  return SCSService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AudioRecorderService = /*#__PURE__*/(() => {
  class AudioRecorderService {
    constructor(resampler, scs) {
      this.resampler = resampler;
      this.scs = scs;
      this._volume = 1.0;
      this.analyser = null;
      this.audioInput = null;
      this.BUFFER_LENGTH = 4096;
      this._device = null;
      this.inputLevel = null;
      this.leftChannel = null;
      this.recorder = null;
      this.retainAudioBuffer = false;
      this.isLongRecording = false;
      this.isWorkLetRecorder = false;
      this.shouldCreateAC = true;
      this.workLetFullySupported = !CWDetection.isIOS;
      this.volumeNode = null;
      this._sampleRate = 41000;
      this.topWin = getTopWindow(window);
      this.init();
    }
    set device(value) {
      if (this._device !== value) {
        this._device = value;
      }
    }
    get device() {
      return this._device;
    }
    /**
     * isMicAllowed
     */
    set isMicAllowed(value) {
      if (this.topWin) {
        this.topWin.isMicAllowed = value;
      }
    }
    get isMicAllowed() {
      return this.topWin ? this.topWin.isMicAllowed : false;
    }
    /**
     * isMicAllowed
     */
    set isMicError(value) {
      if (this.topWin) {
        this.topWin.isMicError = value;
      }
    }
    get isMicError() {
      return this.topWin ? this.topWin.isMicError : false;
    }
    get sampleRate() {
      return this._sampleRate;
    }
    /**
     * Calculate Mic Level and send notification
     */
    get microphoneLevel() {
      if (this.retainAudioBuffer && !this.leftChannel) {
        return -1;
      }
      if (this._volume === 0 || !this.analyser) {
        return 0;
      }
      const arr = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(arr);
      let values = 0;
      for (let i = 0; i < arr.length; i++) {
        values += arr[i];
      }
      return Math.max(Math.ceil(values / arr.length), 0);
    }
    /**
     * Sets the volume level. Allowed values  0 .. 1
     */
    set volume(value) {
      value = Math.max(0, Math.min(1, value));
      if (this._volume !== value) {
        this._volume = value;
        this.updateVolume();
      }
    }
    get volume() {
      const value = this.volumeNode ? this.volumeNode.gain.value : this._volume;
      if (this._volume !== value) {
        this._volume = value;
      }
      return this._volume;
    }
    /**
     * Clean memory leaks
     */
    clean() {
      document.removeEventListener('click', this.onUserGesture, true);
      document.removeEventListener('touchend', this.onUserGesture, true);
      document.removeEventListener('touchstart', this.onUserTouchStart, true);
      this.stopDeviceChangeMonitor();
      if (this.resampler) {
        this.resampler.clean();
      }
      if (this.scs) {
        this.scs.clean();
      }
      this.cleanRecorder();
      if (this.context && this.context.state !== 'closed' && typeof this.context.close === 'function') {
        this.context.close().catch();
      }
      if (this.recorder && this.recorder.port) {
        this.recorder.port.onmessage = null;
      }
      this._aContextByUserGesture = null;
      this.context = null;
      this._device = null;
      this.leftChannel = null;
      this.onMicrophoneLevelChange = null;
      this.onRecordError = null;
      this.mediaStream = null;
      this.recorder = null;
      this.topWin = null;
      this.workerSubject = cleanSubscription(this.workerSubject);
    }
    /**
     * Clean audio process and disconnect from node
     */
    cleanRecorder(shouldStopWorkletCtx = true) {
      this.retainAudioBuffer = false;
      this.isLongRecording = false;
      this.micLevelInterval = cleanSubscription(this.micLevelInterval);
      this.micLevelTimeout = cleanSubscription(this.micLevelTimeout);
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
      }
      if (CWDetection.isIOS) {
        if (this.audioInput) {
          this.audioInput.disconnect();
        }
        if (this.analyser) {
          this.analyser.disconnect();
        }
      } else {
        if (this.volumeNode && this.inputLevel) {
          this.volumeNode.disconnect(this.inputLevel);
        }
        if (this.recorder && this.inputLevel) {
          this.inputLevel.disconnect(this.recorder);
        }
        if (this.audioInput && this.volumeNode) {
          this.audioInput.disconnect(this.volumeNode);
        }
        if (this.audioInput && this.analyser) {
          this.audioInput.disconnect(this.analyser);
        }
      }
      if (this.recorder && typeof this.recorder.disconnect === 'function') {
        this.recorder.disconnect();
        this.recorder.onaudioprocess = null;
      }
      if (this.isWorkLetRecorder) {
        if (shouldStopWorkletCtx && this.context && this.context.state === 'running' && typeof this.context.suspend === 'function') {
          this.context.suspend().catch();
        }
      } else {
        if (this.context && typeof this.context.close === 'function') {
          this.context.close().catch();
        }
        this.recorder = null;
        this.context = null;
        if (this._aContextByUserGesture?.state === 'closed') {
          this._aContextByUserGesture = null;
        }
        this.shouldCreateAC = true;
      }
      this.audioInput = null;
      this.volumeNode = null;
      this.inputLevel = null;
      this.analyser = null;
    }
    /**
     * Start record user voice
     */
    startRecord(retainBuffer = true, device = null, isLongRecording = false) {
      this.leftChannel = null;
      this.cleanRecorder(false);
      this.isLongRecording = isLongRecording;
      return this.setRecorderAudioContext().pipe(mergeMap(isSuccess => {
        if (!isSuccess) {
          return throwError('Can not set audio context');
        }
        this.retainAudioBuffer = retainBuffer;
        this.sendMessageToWorker({
          type: 'retainBuffer',
          retainBuffer
        });
        return this.getMicrophone(device).pipe(mergeMap(stream => {
          this.mediaStream = stream;
          this.isMicAllowed = true;
          this.isMicError = false;
          this.setMediaStream();
          this.monitorDeviceChange();
          return this.getMicrophoneIndicator();
        }), catchError(err => {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            this.isMicAllowed = false;
            this.isMicError = true;
          }
          return throwError(new Error(err.name));
        }));
      }));
    }
    /**
     * Stop record user voice
     */
    stopRecord(getData = true) {
      const time = getData ? this.BUFFER_LENGTH / this.sampleRate * 1000 : 0;
      this.stopDeviceChangeMonitor();
      return timer(time).pipe(mergeMap(() => {
        this.cleanRecorder();
        if (!getData) {
          this.leftChannel = null;
          return of(null);
        } else {
          return this.getBuffers();
        }
      }));
    }
    /**
     * Dummy method for compatibility with native recorder
     */
    turnOffMicrophone() {}
    /**
     * Only for showing microphone activity level
     */
    turnOnMicrophone() {
      this.cleanRecorder(false);
      return this.getMicrophone().pipe(mergeMap(stream => this.setRecorderAudioContext().pipe(mergeMap(() => {
        this.mediaStream = stream;
        this.isMicAllowed = true;
        this.isMicError = false;
        return new Observable(subscriber => {
          subscriber.next(this.isMicAllowed);
          subscriber.complete();
          return () => {
            this.cleanRecorder();
          };
        });
      }))));
    }
    /**
     * Stop record and get recorded data
     * Return user voice recording data
     */
    getBuffers() {
      let obs = of(this.leftChannel);
      if (this.isWorkLetRecorder) {
        this.sendMessageToWorker({
          type: 'getBuffers'
        });
        cleanSubscription(this.workerSubject);
        obs = this.workerSubject = new Subject();
      }
      return obs.pipe(mergeMap(float32Array => {
        if (!float32Array) {
          return of({
            numChannels: 1,
            duration: 0,
            sampleRate: 22050,
            soundData: new Float32Array(0),
            snr: UserSoundQuality.LOW_SNR,
            type: 'pcm'
          });
        }
        let context;
        const win = getHighestWindow(window);
        try {
          context = win.decodeAutioContext;
          if (!context) {
            context = new (win.AudioContext || win.webkitAudioContext || win.mozAudioContext)();
            context.close();
            win.decodeAutioContext = context;
          }
        } catch (e) {
          context = win.decodeAutioContext;
        }
        const audioBuffer = createAudioBuffer(context, float32Array.slice(0), this.sampleRate);
        this.leftChannel = null;
        return this.resampler.resample(audioBuffer).pipe(map(data => {
          const trimmed = trimSilence(data, 22050);
          if (trimmed.length > 0) {
            data = trimmed;
          }
          return {
            numChannels: 1,
            duration: data.length / 22050,
            sampleRate: 22050,
            soundData: data,
            snr: trimmed.length > 0 ? '' : UserSoundQuality.LOW_SNR,
            hasSound: trimmed.length > 0,
            type: 'pcm'
          };
        }));
      }));
    }
    /**
     * Get Cross browser Microphone object
     */
    getMicrophone(evt = null) {
      const nav = navigator;
      if (nav.mediaDevices === undefined) {
        nav['mediaDevices'] = {};
      }
      if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
        navigator.mediaDevices.getUserMedia = constraints => {
          const getUserMedia = nav['getUserMedia'] || nav['webkitGetUserMedia'] || nav['mozGetUserMedia'];
          if (!getUserMedia) {
            return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
          }
          return new Promise((resolve, reject) => getUserMedia.call(navigator, constraints, resolve, reject));
        };
      }
      if (evt) {
        return from(nav.mediaDevices.getUserMedia({
          audio: {
            deviceId: evt.deviceId ? {
              exact: evt.deviceId
            } : undefined
          }
        }));
      }
      return this.getMediaDevices().pipe(mergeMap(device => {
        console.log(`device.label: ${device ? device.label : 'undefined'}`);
        this.device = device;
        return from(nav.mediaDevices.getUserMedia({
          audio: {
            deviceId: device && device.deviceId ? {
              exact: device.deviceId
            } : undefined
          }
        }));
      }));
    }
    /**
     * Emits microphone level
     */
    getMicrophoneIndicator() {
      return new Observable(subscriber => {
        subscriber.next(0);
        this.onMicrophoneLevelChange = subscriber.next.bind(subscriber);
        this.onRecordError = subscriber.error.bind(subscriber);
        this.micLevelInterval = interval(Math.min(this.BUFFER_LENGTH, 4096) / this.sampleRate * 1000).subscribe(() => this.onMicrophoneLevelChange(this.microphoneLevel));
        this.micLevelTimeout = timer(3000).subscribe(() => {
          if (this.isWorkLetRecorder && !this.leftChannel && this.retainAudioBuffer) {
            this.leftChannel = new Float32Array(0);
          }
        });
        return () => {
          this.micLevelInterval = cleanSubscription(this.micLevelInterval);
          this.micLevelTimeout = cleanSubscription(this.micLevelTimeout);
          this.onMicrophoneLevelChange = null;
          this.onRecordError = null;
          this.stopDeviceChangeMonitor();
        };
      });
    }
    /**
     * Checks if media devices are available
     */
    getMediaDevices() {
      const md = navigator.mediaDevices;
      if (md && typeof md.enumerateDevices === 'function') {
        return from(md.enumerateDevices()).pipe(map(devices => devices.filter(device => device.kind.toLowerCase() === 'audioinput').filter(device => device.label.toLowerCase().indexOf('output') === -1)), map(devices => {
          console.log(`Length d: ${devices.length}`);
          if (devices.length > 1 && CWDetection.isModernSafari) {
            devices = devices.filter(device => device.label.toLowerCase().indexOf('built-in input') === -1);
          }
          return devices[0];
        }));
      } else {
        const kind = 'audioinput';
        const mdi = {
          deviceId: 'default',
          label: 'default',
          kind,
          groupId: 'default'
        };
        return of(mdi);
      }
    }
    monitorDeviceChange() {
      this.stopDeviceChangeMonitor();
      let obs;
      if (!CWDetection.isMac) {
        const md = navigator.mediaDevices || null;
        if (md && typeof md.addEventListener === 'function') {
          obs = fromEvent(md, 'devicechange');
        }
      }
      if (!obs) {
        obs = interval(50);
      }
      this.deviceSub = obs.pipe(mergeMap(() => this.getMediaDevices()), mergeMap(newDevice => {
        const oldDevice = this.device;
        if (!newDevice) {
          return of(newDevice);
        } else if (oldDevice && newDevice.deviceId !== oldDevice.deviceId) {
          this.device = newDevice;
          console.log('Mic changed');
          this.cleanRecorder();
          return this.getMicrophone().pipe(mergeMap(stream => {
            this.mediaStream = stream;
            this.isMicAllowed = true;
            this.isMicError = false;
            return this.setRecorderAudioContext().pipe(mergeMap(isSuccess => {
              if (!isSuccess) {
                return throwError('Can not set audio context');
              }
              this.setMediaStream();
              return of(newDevice);
            }));
          }));
        } else {
          return of(newDevice);
        }
      })).subscribe(newDevice => {
        if (!newDevice) {
          this.device = null;
          this.cleanRecorder();
          this.stopDeviceChangeMonitor();
          if (this.onRecordError !== null) {
            this.onRecordError(new Error('Device changed'));
          }
        }
      }, err => {
        console.log(err);
      });
    }
    /**
     * Initialize
     */
    init() {
      if (this.topWin && this.topWin.isMicAllowed) {
        this.isMicAllowed = true;
      }
      document.addEventListener('click', this.onUserGesture.bind(this), true);
      document.addEventListener('touchend', this.onUserGesture.bind(this), true);
      document.addEventListener('touchstart', this.onUserTouchStart.bind(this), true);
    }
    /**
     * Handle audio process event when dispatched
     */
    onAudioProgress(evt) {
      if (this.retainAudioBuffer) {
        const currentBuffer = evt.inputBuffer ? evt.inputBuffer.getChannelData(0) : evt.data.inputBuffer || [];
        const len = currentBuffer.length;
        if (!this.leftChannel) {
          this.leftChannel = currentBuffer.slice(0);
        } else {
          const recordingLength = this.leftChannel.length;
          const newLength = recordingLength + len;
          const newBuffer = new Float32Array(newLength);
          newBuffer.set(this.leftChannel, 0);
          newBuffer.set(currentBuffer, recordingLength);
          this.leftChannel = newBuffer;
        }
      }
    }
    onUserGesture() {
      if (!this._aContextByUserGesture || this._aContextByUserGesture.state === 'closed' || this.shouldCreateAC) {
        const win = window;
        this.shouldCreateAC = false;
        this._aContextByUserGesture = new (win.AudioContext || win.webkitAudioContext || win.mozAudioContext)();
        if (this._aContextByUserGesture && ['interrupted', 'suspended'].includes(this._aContextByUserGesture.state)) {
          try {
            this._aContextByUserGesture.resume().catch();
          } catch (e) {
            console.warn(`New context created with ${this._aContextByUserGesture.state} state. Things can go wrong...`);
          }
        }
      }
    }
    onUserTouchStart(evt) {
      const evtTarget = evt.target;
      if (evtTarget?.parentElement?.nodeName === 'RECORD-BUTTON') {
        if (this.isWorkLetRecorder && this.context) {
          if (['interrupted', 'suspended'].includes(this.context.state)) {
            try {
              this.context.resume().catch();
            } catch (e) {
              console.warn(`New context created with ${this.context.state} state. Things can go wrong...`);
            }
          }
        }
      }
    }
    onWorkerMessage(evt) {
      const message = evt.data || {};
      if (message.type === 'buffers' && this.workerSubject) {
        this.workerSubject.next(message.buffers);
        this.workerSubject.complete();
      } else if (message.type === 'firstBuffer') {
        this.leftChannel = message.buffers;
      }
    }
    sendMessageToWorker(msg) {
      if (this.recorder && this.recorder.port) {
        this.recorder.port.postMessage(msg);
      }
    }
    /**
     * Create audio context and processor
     */
    setRecorderAudioContext() {
      const obs = of(null);
      const win = window;
      try {
        this.context = (this.isWorkLetRecorder ? this.context : null) || this._aContextByUserGesture || new (win.AudioContext || win.webkitAudioContext || win.mozAudioContext)();
      } catch (e) {
        console.warn('Can not create context for recording');
        return of(false);
      }
      if (this.context.state === 'closed') {
        console.warn(`New context created with ${this.context.state} state. Things can go wrong...`);
        return of(false);
      }
      if (['interrupted', 'suspended'].includes(this.context.state)) {
        try {
          this.context.resume().catch();
        } catch (e) {
          console.warn(`New context created with ${this.context.state} state. Things can go wrong...`);
          return of(false);
        }
      }
      const ctx = this.context;
      this.BUFFER_LENGTH = this.isLongRecording ? 16384 : 4096;
      return obs.pipe(mergeMap(() => {
        if (this.workLetFullySupported && this.context && this.context.audioWorklet) {
          this.BUFFER_LENGTH = 128;
          if (!this.recorder) {
            return from(this.context.audioWorklet.addModule(getScript())).pipe(tap(() => {
              this.recorder = new AudioWorkletNode(this.context, 'processor-script', {
                channelCount: 1
              });
              this.isWorkLetRecorder = true;
            }));
          }
        } else if (ctx && typeof ctx['createJavaScriptNode'] === 'function') {
          this.recorder = ctx['createJavaScriptNode'](this.BUFFER_LENGTH, 1, 1);
        } else if (ctx && ctx.createScriptProcessor) {
          this.recorder = ctx.createScriptProcessor(this.BUFFER_LENGTH, 1, 1);
        }
        return of(null);
      }), map(() => {
        if (this.recorder && typeof this.recorder.connect === 'function') {
          this.recorder.connect(this.context.destination);
        }
        return true;
      }));
    }
    /**
     * Set media stream
     */
    setMediaStream() {
      this.isMicAllowed = true;
      this.isMicError = false;
      if (this.mediaStream) {
        this._sampleRate = this.context.sampleRate;
        this.audioInput = this.context.createMediaStreamSource(this.mediaStream);
        this.analyser = this.context.createAnalyser();
        this.analyser.fftSize = 32;
        this.audioInput.connect(this.analyser);
        if (!CWDetection.isIOS) {
          this.volumeNode = this.context.createGain();
          this.inputLevel = this.context.createGain();
          this.volumeNode.gain.value = this._volume;
          this.inputLevel.gain.value = this._volume;
          this.volumeNode.connect(this.inputLevel);
          this.audioInput.connect(this.volumeNode);
          this.inputLevel.connect(this.recorder);
        } else {
          this.audioInput.connect(this.recorder);
        }
        if (this.recorder.port) {
          this.recorder.port.onmessage = this.onWorkerMessage.bind(this);
        } else {
          this.recorder.onaudioprocess = this.onAudioProgress.bind(this);
        }
      }
    }
    stopDeviceChangeMonitor() {
      this.deviceSub = cleanSubscription(this.deviceSub);
    }
    updateVolume() {
      this._volume = Math.max(0, this._volume);
      if (this.volumeNode && this.volumeNode.gain) {
        this.volumeNode.gain.value = this._volume;
      }
    }
    static {
      this.ɵfac = function AudioRecorderService_Factory(t) {
        return new (t || AudioRecorderService)(i0.ɵɵinject(AudioResamplerService), i0.ɵɵinject(SCSService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AudioRecorderService,
        factory: AudioRecorderService.ɵfac
      });
    }
  }
  return AudioRecorderService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var SessionStatus = EV.SessionStatus;
let EvapiScsService = /*#__PURE__*/(() => {
  class EvapiScsService {
    constructor() {
      this.isAvailable = true;
      this.connectionStatus = new BehaviorSubject(this.isAvailable);
      this.apiVersion = 2;
      this.serverUrl = '//sound.transparent.com/jsndcmp/sound/';
      this.serverUrlV2 = '//sound.transparent.com/jsndcmp/v2/sound/';
    }
    clean() {
      this.config = null;
      this.connectionStatus = null;
      this.evApi = null;
      this.resolver = null;
    }
    /**
     * Free a slot when a user completes or navigates away from an activity
     */
    destroySession() {
      this.evApi?.closeSession();
      return of('');
    }
    /**
     *
     */
    compareAIM(card, userSound, scsConfig, side = 2) {
      return new Observable(observer => {
        const files = getMasterSounds(card.answers || [card]);
        files.forEach(file => file.url = this.resolveMediaUrl(file.url));
        const answer = getMasterSounds([card])[0];
        answer.url = this.resolveMediaUrl(answer.url);
        this.lastComparisonId = UIDUtil.createUID();
        let sub = (this.evApi ? this.evApi.compare(this.lastComparisonId, 'wav', exportWAV(userSound), files, answer) : of({
          status: EV.ComparisonStatus.DONE
        })).pipe(catchError(() => of({
          status: EV.ComparisonStatus.DONE
        })), filter(data => {
          console.log(data);
          return data.status === EV.ComparisonStatus.DONE || data.status === EV.ComparisonStatus.FAILED;
        })).subscribe(data => {
          const result = this.processAIMResult(data, card);
          result.ticket = this.lastComparisonId;
          const snr = scsConfig.minSnr || 15;
          let quality;
          try {
            quality = UserSoundQuality.check(userSound, snr);
          } catch (error) {
            quality = {
              result: UserSoundQuality.LOW_SNR,
              snr: -1
            };
          }
          result.snr = quality.result;
          result.masterVADResults = null;
          result.userVadResult = data.userSoundDetails ? data.userSoundDetails.vad : null;
          result.userSoundKey = this.normalizeUrl(data.userSoundUrl);
          observer.next(result);
          observer.complete();
        });
        return () => {
          if (sub && !sub.closed) {
            sub.unsubscribe();
          }
        };
      });
    }
    /**
     * Request will return part of service settings
     */
    getConfig(url) {
      this.serverUrl = url || this.serverUrl;
      const pattern = `v${this.apiVersion}`;
      if (!this.serverUrl.includes(pattern)) {
        this.serverUrlV2 = this.serverUrl.replace('jsndcmp/sound', `jsndcmp/v${this.apiVersion}/sound`);
      } else {
        this.serverUrlV2 = this.serverUrl;
      }
      const handleFailure = () => {
        this.isAvailable = false;
        this.connectionStatus.next(this.isAvailable);
        return throwError('EVAPI not initialized!');
      };
      const win = window;
      // EV.APIFactory.version = '1.0';
      return race(from(EV.APIFactory.init(url.replace(/\/sound\/*$/, ''), win['evapiurl'])), timer(3000).pipe(mergeMap(() => handleFailure()))).pipe(first(), catchError(() => handleFailure()), mergeMap(evApi => {
        this.evApi = evApi;
        if (evApi?.getConfig) {
          return evApi.getConfig();
        }
        return handleFailure();
      }), map(conf => {
        this.config = conf;
        return this.config;
      }));
    }
    /**
     * Uploads list of sounds to scs server,
     * Get Missing List Files
     * loads and auploads them
     */
    getFiles(cards, scsConfig, side = 2) {
      let obs = of(null);
      const win = window;
      if (typeof win.CWAPI?.list?.getMediaElementResolver === 'function') {
        obs = from(win.CWAPI.list.getMediaElementResolver());
      }
      return obs.pipe(mergeMap(resolver => {
        this.resolver = resolver;
        const files = getMasterSounds(cards);
        files.forEach(file => file.url = this.resolveMediaUrl(file.url));
        return (this.evApi ? this.evApi.startSession(scsConfig, files) : of(SessionStatus.OFFLINE)).pipe(tap(status => {
          if (status) {
            this.isAvailable = status === SessionStatus.OK;
            this.connectionStatus.next(this.isAvailable);
          }
        }));
      }));
    }
    /**
     * A polling method for checking server connectivity
     */
    getVersion() {
      return this.connectionStatus;
    }
    /**
     * Sets iwascorrect flag in the log.
     */
    iWasCorrect() {
      const res = this.evApi ? this.evApi.setIWasCorrect(this.lastComparisonId) : {};
      return typeof res.resolve === 'function' ? from(res) : of('');
    }
    /**
     * Adds sound url to the given user sound key
     */
    normalizeUrl(userSoundKey) {
      if (userSoundKey && !/^(https?:)?\/\//i.test(userSoundKey)) {
        userSoundKey = `${this.serverUrlV2}usersound/${userSoundKey}`;
      }
      return userSoundKey;
    }
    processAIMResult(data, card) {
      let correctAnswerIndex = -1;
      let isCorrect = false;
      const answers = card.answers || [card];
      const answersLength = answers.length;
      for (let i = 0; i < answersLength; i++) {
        if (answers[i].isCorrect) {
          correctAnswerIndex = i;
        }
      }
      let aimValue = 0;
      const aimVO = new AimResult();
      if (data.result) {
        aimValue = +data.result.score;
        isCorrect = data.status === EV.ComparisonStatus.DONE && data.result.closestMaster === correctAnswerIndex && Math.max(0, aimValue) !== 0;
        aimVO.isAllMinusOne = data.status === EV.ComparisonStatus.FAILED;
        // Sometimes server may send response scores like [0.99, 0.99, 0.99, 0.99].
        // It means that user is incorrect.
        aimVO.resultsAreEqual = data.result.closestMaster === null;
        aimVO.resultAnswerIndex = data.result.closestMaster;
        aimVO.segments = data.result.segments;
      }
      aimVO.isCorrect = isCorrect;
      aimVO.aimValue = aimValue;
      aimVO.userDetails = data.userSoundDetails;
      aimVO.answerIndex = correctAnswerIndex;
      return aimVO;
    }
    resolveMediaUrl(url) {
      if (typeof this.resolver === 'function') {
        if (/\.zip|\.b4x/g.test(url)) {
          url = 'b4x:' + url.replace(/^https?:/, '');
        }
        url = this.resolver(url);
      }
      return url;
    }
    static {
      this.ɵfac = function EvapiScsService_Factory(t) {
        return new (t || EvapiScsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EvapiScsService,
        factory: EvapiScsService.ɵfac
      });
    }
  }
  return EvapiScsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const STRIP_TAGS = /<\/?[^>]+(>|$)/igm;
/**
 * Get lsit of unavailable files
 */
function getMasterSounds(cards) {
  const files = [];
  const len = cards.length;
  for (let i = 0; i < len; i++) {
    const card = cards[i];
    const file = {};
    file.card = {
      hid: card.hid,
      lessonId: card.listId || card.sourceId,
      side1phrase: {
        text: cleanTags(card.cardSide1Text),
        hint: cleanTags(card.cardSide1Hint),
        translit: cleanTags(card.cardTranslit)
      },
      side2phrase: {
        text: cleanTags(card.cardSide2Text),
        hint: cleanTags(card.cardSide2Hint),
        translit: cleanTags(card.cardTranslit)
      }
    };
    file.url = card.soundSide2;
    const pat = /^(https?)|(file:\/\/)/;
    if (!pat.test(file.url)) {
      file.url = location.protocol + file.url;
    }
    files.push(file);
  }
  return files;
}
function cleanTags(str) {
  return (str || '').replace(STRIP_TAGS, '');
}

/**
 * Duration of sound
 */
const SOUND_DURATION = 'soundDuration';
/**
 * Sound Record Finish event
 */
const SOUND_DATA_LOAD_ERROR = 'soundDataLoadError';
/**
 * Sent when audio dispatches an error
 */
const SOUND_PLAY_ERROR = 'soundPlayError';
/**
 * Sent when sound trimmed
 */
const SOUND_TRIMMED = 'SoundTrimmed';
/**
 * Sent when sound position changed
 */
const MIC_ACTIVITY_LEVEL_CHANGED = 'micActivityLevelChanged';
const SOUND_COMPLETE = 'soundComplete';
const SOUND_DATA_LOADED = 'soundDataLoaded';
const PART_OF_BYTES = 'partOfBytes';
const POSITION_CHANGED = 'positionChanged';
const SWF_LOADED = 'swfLoaded';
const RECORDING_FINISHED = 'recordingFinished';
const MIC_NOT_ALLOWED = 'micNotAllowed';
const MIC_ALLOWED = 'micAllowed';
const MICROPHONE_ACTIVITY_LEVEL = 'microphoneActivityLevel';
const RECORD_STOP = 'recordStop';
const ERROR = 'error';
const SoundEvent = {
  SOUND_DURATION,
  SOUND_DATA_LOAD_ERROR,
  SOUND_PLAY_ERROR,
  SOUND_TRIMMED,
  MIC_ACTIVITY_LEVEL_CHANGED,
  SOUND_COMPLETE,
  SOUND_DATA_LOADED,
  PART_OF_BYTES,
  POSITION_CHANGED,
  SWF_LOADED,
  RECORDING_FINISHED,
  MIC_NOT_ALLOWED,
  MIC_ALLOWED,
  MICROPHONE_ACTIVITY_LEVEL,
  RECORD_STOP,
  ERROR
};
function downSamplePCM(buffer, sampleRate = 22050, targetSampleRate = 16000) {
  const sampleRateRatio = sampleRate / targetSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = accum / count;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}
class CallbackRecorder {
  constructor(zone) {
    this.zone = zone;
  }
  clean() {
    this.onMicActivityLevelChange = null;
    this.onSoundTrimmed = null;
    this.onMicAllowed = null;
    this.onMicNotAllowed = null;
    this.onRecordingFinished = null;
    this.onFlashRecordingFinished = null;
    this.onSwfLoaded = null;
    this.onBytesReceived = null;
    this.onPositionChange = null;
    this.onDurationChange = null;
    this.onSoundDataLoadError = null;
    this.onSoundPlayError = null;
    this.onSoundPlayComplete = null;
    this.onSoundDataLoaded = null;
    this.onErrorChange = null;
  }
  /**
   * SoundRecorder callback
   */
  onNativeRecorderCallback(name, data) {
    switch (name) {
      case MICROPHONE_ACTIVITY_LEVEL:
      case MIC_ACTIVITY_LEVEL_CHANGED:
        if (typeof this.onMicActivityLevelChange === 'function') {
          if (CWDetection.isIOS) {
            data = Math.abs(parseInt(data, 10)) * 5;
          }
          this.zone.run(() => this.onMicActivityLevelChange(data));
        }
        break;
      case SOUND_TRIMMED:
        if (typeof this.onSoundTrimmed === 'function') {
          this.zone.run(() => this.onSoundTrimmed(data));
        }
        break;
      case MIC_ALLOWED:
        if (typeof this.onMicAllowed === 'function') {
          this.zone.run(() => this.onMicAllowed(data));
        }
        break;
      case MIC_NOT_ALLOWED:
        if (typeof this.onMicNotAllowed === 'function') {
          this.zone.run(() => this.onMicNotAllowed(data));
        }
        break;
      case RECORD_STOP:
        if (typeof this.onFlashRecordingFinished === 'function') {
          this.zone.run(() => this.onFlashRecordingFinished(data));
        }
        break;
      case RECORDING_FINISHED:
        if (typeof this.onRecordingFinished === 'function') {
          this.zone.run(() => this.onRecordingFinished(data));
        }
        break;
      case SWF_LOADED:
        if (typeof this.onSwfLoaded === 'function') {
          this.zone.run(() => this.onSwfLoaded());
        }
        break;
      case POSITION_CHANGED:
        if (typeof this.onPositionChange === 'function') {
          this.zone.run(() => this.onPositionChange(data.posProps.position));
        }
        break;
      case SOUND_COMPLETE:
        if (typeof this.onSoundPlayComplete === 'function') {
          this.zone.run(() => this.onSoundPlayComplete());
        }
        break;
      case PART_OF_BYTES:
        if (typeof this.onBytesReceived === 'function') {
          this.zone.run(() => this.onBytesReceived(data));
        }
        break;
      case SOUND_DATA_LOADED:
        if (typeof this.onSoundDataLoaded === 'function') {
          this.zone.run(() => this.onSoundDataLoaded());
        }
        break;
      case SOUND_DATA_LOAD_ERROR:
        if (typeof this.onSoundDataLoadError === 'function') {
          this.zone.run(() => this.onSoundDataLoadError(data));
        }
        break;
      case SOUND_PLAY_ERROR:
        if (typeof this.onSoundPlayError === 'function') {
          this.zone.run(() => this.onSoundPlayError());
        }
        break;
      case SOUND_DURATION:
        if (typeof this.onDurationChange === 'function') {
          this.zone.run(() => this.onDurationChange(data.duration));
        }
        break;
      case ERROR:
        if (typeof this.onErrorChange === 'function' && data) {
          this.zone.run(() => this.onErrorChange(data));
        }
        break;
    }
  }
}
let MobileAudioService = /*#__PURE__*/(() => {
  class MobileAudioService {
    constructor(audioLoaderService, scs, zone) {
      this.audioLoaderService = audioLoaderService;
      this.scs = scs;
      this.zone = zone;
      this._volume = 0.9;
      this.SAMPLE_RATE = 22050;
      this.topWin = getTopWindow(window);
      this.init();
    }
    /**
     * isMicAllowed
     */
    set isMicAllowed(value) {
      if (this.topWin) {
        this.topWin.isMicAllowed = value;
      }
    }
    get isMicAllowed() {
      return this.topWin ? this.topWin.isMicAllowed : false;
    }
    /**
     * isMicAllowed
     */
    set isMicError(value) {
      if (this.topWin) {
        this.topWin.isMicError = value;
        if (value) {
          this.topWin.navigator.cwMediaStream = null;
        }
      }
    }
    get isMicError() {
      return this.topWin ? this.topWin.isMicError : false;
    }
    get sampleRate() {
      return this.SAMPLE_RATE;
    }
    /**
     * Sets the volume level. Allowed values  0 .. 1
     */
    set volume(value) {
      value = Math.max(0, Math.min(1, value));
      if (this._volume !== value) {
        this._volume = value;
      }
    }
    get volume() {
      return this._volume;
    }
    clean() {
      if (this.nativeRecorder) {
        this.nativeRecorder.setCallback(null);
      }
      if (this.callbackRecorder) {
        this.callbackRecorder.clean();
      }
      if (this.scs) {
        this.scs.clean();
      }
      this.callbackRecorder = null;
      this.nativeRecorder = null;
    }
    /**
     * Clean
     */
    cleanRecorder() {}
    /**
     * Start record user voice
     */
    startRecord(retainBuffer = false) {
      return this.turnOnMicrophone().pipe(mergeMap(() => new Observable(subscriber => {
        this.callbackRecorder.onMicActivityLevelChange = subscriber.next.bind(subscriber);
        if (this.nativeRecorder) {
          this.nativeRecorder.startRecord();
        }
        return () => {
          this.callbackRecorder.onErrorChange = null;
          this.callbackRecorder.onMicActivityLevelChange = null;
        };
      })));
    }
    /**
     * Stop record user voice
     */
    stopRecord(getData = true) {
      if (!getData) {
        if (this.nativeRecorder) {
          this.nativeRecorder.stopRecord();
          this.nativeRecorder.turnOffMicrophone(); // Don't remove
          this.nativeRecorder.stopRecord.bind(this.nativeRecorder);
        }
        return of(null);
      }
      return new Observable(subscriber => {
        this.callbackRecorder.onRecordingFinished = url => {
          subscriber.next(url);
          subscriber.complete();
        };
        if (this.nativeRecorder) {
          this.nativeRecorder.stopRecord();
          this.nativeRecorder.stopRecord.bind(this.nativeRecorder);
        }
        return () => {
          this.callbackRecorder.onErrorChange = null;
          this.callbackRecorder.onRecordingFinished = null;
        };
      }).pipe(mergeMap(url => {
        this.turnOffMicrophone(); // Don't remove
        return this.audioLoaderService.loadRecordedSound(url);
      }));
    }
    /**
     * Trim given sound
     */
    trim(url, start, end) {
      return new Observable(subscriber => {
        this.callbackRecorder.onSoundTrimmed = val => {
          subscriber.next(val);
          subscriber.complete();
        };
        if (this.nativeRecorder) {
          this.nativeRecorder.trim(url, start, end);
        }
        return () => {
          this.callbackRecorder.onErrorChange = null;
          this.callbackRecorder.onSoundTrimmed = null;
        };
      });
    }
    /**
     * Turn off microphone
     */
    turnOffMicrophone() {
      if (this.nativeRecorder) {
        this.nativeRecorder.turnOffMicrophone();
      }
    }
    /**
     * Turn microphone on after user allowing security popup
     * sends message {name:'micActivityLevelChanged', data : level} level 0 to 100
     * if user allow microphone security popup
     * then sends message {name:'micAllowed', data:null}
     * Only for showing microphone activity level
     * for Speech Gate view with speech = false
     */
    turnOnMicrophone() {
      return new Observable(subscriber => {
        this.callbackRecorder.onMicNotAllowed = err => {
          this.isMicAllowed = false;
          this.isMicError = true;
          subscriber.error(err);
          subscriber.complete();
        };
        this.callbackRecorder.onMicAllowed = data => {
          this.isMicAllowed = true;
          this.isMicError = false;
          subscriber.next(true);
          subscriber.complete();
        };
        if (this.nativeRecorder) {
          this.nativeRecorder.turnOnMicrophone();
          this.nativeRecorder.turnOffMicrophone.bind(this.nativeRecorder);
        }
        return () => {
          this.callbackRecorder.onErrorChange = null;
          this.callbackRecorder.onMicNotAllowed = null;
          this.callbackRecorder.onMicAllowed = null;
        };
      });
    }
    /**
     * Initialize
     */
    init() {
      this.nativeRecorder = window.upSoundRecorder;
      this.callbackRecorder = new CallbackRecorder(this.zone);
      if (this.nativeRecorder) {
        this.nativeRecorder.setCallback(this.callbackRecorder.onNativeRecorderCallback.bind(this.callbackRecorder));
      }
    }
    static {
      this.ɵfac = function MobileAudioService_Factory(t) {
        return new (t || MobileAudioService)(i0.ɵɵinject(AudioLoaderService), i0.ɵɵinject(SCSService), i0.ɵɵinject(i0.NgZone));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MobileAudioService,
        factory: MobileAudioService.ɵfac
      });
    }
  }
  return MobileAudioService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Packager does not see import in proiders therefore moved here
 */
function recorderFactory(audioResampler, audioLoaderService, scs, zone) {
  const isIOS = CWDetection.isIOS;
  const isMobileNative = CWDetection.getIsNative(window);
  if (isMobileNative) {
    return new MobileAudioService(audioLoaderService, scs, zone);
  } else if (isIOS && !isMobileNative && !CWDetection.isModernSafari) {
    return new MobileAudioService(audioLoaderService, scs, zone);
  } else {
    return new AudioRecorderService(audioResampler, scs);
  }
}
const recorderServiceProvider = {
  provide: AudioRecorderService,
  useFactory: recorderFactory,
  deps: [AudioResamplerService, AudioLoaderService, SCSService, NgZone]
};
let AudioModule = /*#__PURE__*/(() => {
  class AudioModule {
    static {
      this.ɵfac = function AudioModule_Factory(t) {
        return new (t || AudioModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AudioModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [AudioResamplerService, MobileAudioService, AudioPlayerService, recorderServiceProvider, AudioLoaderService, SCSService, EvapiScsService],
        imports: [CommonModule, HttpClientModule]
      });
    }
  }
  return AudioModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of audio
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AimResult, AudioLoaderService, AudioModule, AudioPlayerService, AudioRecorderService, AudioResamplerService, EvapiScsService, SCSActivityType, SCSService, SoundEvent, UserSoundQuality, WavUtil, WindowUtil, calculateSoundPeak, decodeWav, downSamplePCM, fixTime, getAudioContext, getPaddings };
